/**
 * Mobile Design Studio Controller
 * Handles all mobile-specific interactions for the product configurator
 * CustomInk-style interface with floating buttons, bottom nav, and slide-up panels
 */

(function() {
  'use strict';

  if (typeof window !== 'undefined' && typeof window.updateSideNavActiveState !== 'function') {
    window.updateSideNavActiveState = function() {};
  }

  // ============================================
  // CONFIGURATION
  // ============================================
  const MDS_CONFIG = {
    breakpoint: 768,
    panelAnimationDuration: 400,
    debounceDelay: 150,
    defaultSides: ['front', 'back'] // Fallback sides
  };

  // ============================================
  // STATE
  // ============================================
  const mdsState = {
    isMobile: false,
    activePanel: null,
    currentSide: 'front',
    hasDesign: false,
    availableSides: [],
    uploadedDesigns: {}, // Track uploads per side: { front: [url0, url1], back: [url0] }
    uploadCounts: {}, // Track upload count per side for indexing: { front: 2, back: 1 }
    textLayers: [], // Track text layers for editing
    editingTextId: null, // Currently editing text layer ID
    editingTextElement: null, // Currently editing text DOM element
    selectedTextColor: '#1a1a2e', // Selected text color
    pendingAutoOpenColors: false, // Flag to auto-open colors panel after upload
    listenersBound: false,
    pendingColorRefreshTimer: null,
    initialSyncCompleted: false,
    initialSyncTimer: null,
    pendingStartupColor: ''
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  /**
   * Convert rgb(r, g, b) string to hex color
   */
  function rgbToHex(rgb) {
    if (!rgb) return null;
    if (rgb.startsWith('#')) return rgb;
    
    const match = rgb.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (!match) return null;
    
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  }
  
  /**
   * Populate mobile Pantone color grid
   */
  // Max visible Pantone swatches to show initially (same as desktop)
  const MDS_PANTONE_MAX_VISIBLE = 50;
  
  function populateMobilePantoneGrid(gridEl) {
    if (!gridEl) return;
    
    // Get Pantone colors from global window.pantoneColors (set by colors.js)
    // This includes the full allPantoneColors set from colors.js
    const pantones = (window.pantoneColors && Object.keys(window.pantoneColors).length > 0)
      ? window.pantoneColors
      : getDefaultPantoneSubset();
    
    console.log(`📱 Using ${Object.keys(pantones).length} Pantone colors for mobile picker`);
    
    gridEl.innerHTML = '';
    let count = 0;
    
    Object.entries(pantones).forEach(([name, hex]) => {
      const swatch = document.createElement('button');
      swatch.className = 'mds-pantone-swatch';
      swatch.dataset.name = name;
      swatch.dataset.hex = hex;
      swatch.style.cssText = `
        width: 28px;
        height: 28px;
        min-width: 28px;
        min-height: 28px;
        background: ${hex};
        border: 2px solid ${hex === '#FFFFFF' ? '#e5e7eb' : 'transparent'};
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;
        padding: 0;
        margin: 0;
      `;
      swatch.title = name;
      
      // Initially hide swatches beyond max visible
      count++;
      if (count > MDS_PANTONE_MAX_VISIBLE) {
        swatch.style.display = 'none';
      }
      
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('📱 Pantone color selected:', name, hex);
        mdsState.selectedTextColor = hex;
        
        // Update color button
        const colorBtn = document.getElementById('mds-text-color');
        if (colorBtn) colorBtn.style.backgroundColor = hex;
        
        // Update desktop text tool if available
        const desktopColorInput = document.getElementById('text-tool-color');
        if (desktopColorInput) {
          desktopColorInput.value = hex;
          desktopColorInput.dispatchEvent(new Event('change'));
        }
        const desktopSwatch = document.getElementById('text-tool-color-swatch');
        if (desktopSwatch) desktopSwatch.style.backgroundColor = hex;
        
        // Hide picker
        const picker = document.getElementById('mds-text-color-picker');
        if (picker) picker.classList.add('hidden');
        
        // Highlight selected swatch
        gridEl.querySelectorAll('.mds-pantone-swatch').forEach(s => {
          s.style.borderColor = s === swatch ? '#4361ee' : (s.dataset.hex === '#FFFFFF' ? '#e5e7eb' : 'transparent');
        });
      });
      
      gridEl.appendChild(swatch);
    });
    
    console.log(`📱 Populated ${count} Pantone colors, showing first ${MDS_PANTONE_MAX_VISIBLE}`);
  }
  
  /**
   * Filter mobile Pantone swatches by search query
   */
  function filterMobilePantoneSwatches(gridEl, query) {
    if (!gridEl) return;
    
    const swatches = gridEl.querySelectorAll('.mds-pantone-swatch');
    const q = (query || '').toLowerCase().trim();
    const numericQuery = q.replace(/[^0-9]/g, '');
    let shown = 0;
    
    swatches.forEach(swatch => {
      const name = (swatch.dataset.name || '').toLowerCase();
      let matches = !q || name.includes(q);
      
      // Also match numeric codes (e.g., "185" matches "185 C")
      if (!matches && numericQuery) {
        const numericName = name.replace(/[^0-9]/g, '');
        if (numericName && numericName.includes(numericQuery)) {
          matches = true;
        }
      }
      
      if (!matches || shown >= MDS_PANTONE_MAX_VISIBLE) {
        swatch.style.display = 'none';
      } else {
        swatch.style.display = '';
        shown++;
      }
    });
  }
  
  /**
   * Get text metadata for an overlay from various sources
   */
  function getTextMetadataForOverlay(side, index, overlayEl) {
    // Try UnifiedStateManager first
    if (typeof window.unifiedStateManager?.getImage === 'function') {
      const imageData = window.unifiedStateManager.getImage(side, index);
      if (imageData?.meta?.type === 'text-tool') {
        return {
          id: overlayEl?.id || `design-overlay-${side}-${index}`,
          element: overlayEl,
          text: imageData.meta.text || '',
          fontFamily: imageData.meta.fontFamily || 'Arial',
          fontSize: imageData.meta.fontSize || 48,
          color: imageData.meta.color || '#1a1a2e',
          bold: imageData.meta.bold || false,
          italic: imageData.meta.italic || false
        };
      }
    }
    
    // Try global text overlay metadata
    if (typeof window.getTextOverlayMetadata === 'function') {
      const meta = window.getTextOverlayMetadata(side, index);
      if (meta) {
        return {
          id: overlayEl?.id || `design-overlay-${side}-${index}`,
          element: overlayEl,
          text: meta.text || meta.originalText || '',
          fontFamily: meta.fontFamily || 'Arial',
          fontSize: meta.fontSize || 48,
          color: meta.color || '#1a1a2e',
          bold: meta.bold || false,
          italic: meta.italic || false
        };
      }
    }
    
    // Fallback: try to read from desktop form if it was just populated
    const desktopTextInput = document.getElementById('text-tool-input');
    const desktopFontSelect = document.getElementById('text-tool-font');
    const desktopSizeInput = document.getElementById('text-tool-size');
    const desktopColorInput = document.getElementById('text-tool-color');
    
    if (desktopTextInput?.value) {
      return {
        id: overlayEl?.id || `design-overlay-${side}-${index}`,
        element: overlayEl,
        text: desktopTextInput.value,
        fontFamily: desktopFontSelect?.value || 'Arial',
        fontSize: parseInt(desktopSizeInput?.value || '48', 10),
        color: desktopColorInput?.value || '#1a1a2e',
        bold: false,
        italic: false
      };
    }
    
    return null;
  }
  
  /**
   * Default Pantone subset for mobile (if global not available)
   */
  function getDefaultPantoneSubset() {
    return {
      'Black C': '#2D2926',
      'White C': '#FFFFFF',
      'Cool Gray 1 C': '#D9D9D6',
      'Cool Gray 5 C': '#B1B3B3',
      'Cool Gray 11 C': '#53565A',
      'Red 032 C': '#EF3340',
      'Orange 021 C': '#FE5000',
      'Yellow C': '#FEDD00',
      'Green C': '#00AB84',
      'Blue 072 C': '#10069F',
      'Reflex Blue C': '#001489',
      'Purple C': '#BB29BB',
      'Rhodamine Red C': '#E10098',
      'Process Cyan C': '#009FDF',
      'Process Magenta C': '#D0006F',
      'Process Yellow C': '#FFE900',
      'Process Black C': '#2D2926',
      '185 C': '#E4002B',
      '186 C': '#C8102E',
      '187 C': '#A6192E',
      '280 C': '#012169',
      '281 C': '#00205B',
      '282 C': '#041E42',
      '485 C': '#DA291C',
      '541 C': '#003C71',
      '7455 C': '#3A5DAE',
      '7462 C': '#166A8F',
      '7473 C': '#279989',
      '7489 C': '#74AA50',
      '7499 C': '#F1E6B2'
    };
  }

  // ============================================
  // DOM ELEMENTS
  // ============================================
  let elements = {};

  function cacheElements() {
    elements = {
      // Quick bar & floating buttons
      quickBar: document.querySelector('.mds-quick-bar'),
      floatingActions: document.getElementById('mds-floating-actions'),
      uploadBtn: document.getElementById('mds-upload-btn'),
      textBtn: document.getElementById('mds-text-btn'),
      
      // Side navigation
      sideNav: document.getElementById('mds-side-nav'),
      
      // Design indicators
      designBadge: document.getElementById('mds-design-badge'),
      tapIndicator: document.getElementById('mds-tap-indicator'),
      
      // Bottom navigation
      bottomNav: document.getElementById('mds-bottom-nav'),
      navItems: document.querySelectorAll('.mds-nav-item[data-panel]'),
      navNext: document.getElementById('mds-nav-next'),
      
      // Panels
      panelBackdrop: document.getElementById('mds-panel-backdrop'),
      uploadPanel: document.getElementById('mds-upload-panel'),
      textPanel: document.getElementById('mds-text-panel'),
      colorsPanel: document.getElementById('mds-colors-panel'),
      productColorsPanel: document.getElementById('mds-product-colors-panel'),
      
      // Panel content elements
      uploadSideTabs: document.getElementById('mds-upload-side-tabs'),
      textSideTabs: document.getElementById('mds-text-side-tabs'),
      textInput: document.getElementById('mds-text-input'),
      fontSelect: document.getElementById('mds-font-select'),
      textSize: document.getElementById('mds-text-size'),
      sizeValue: document.getElementById('mds-size-value'),
      addTextBtn: document.getElementById('mds-add-text'),
      textColor: document.getElementById('mds-text-color'),
      detectedColors: document.getElementById('mds-detected-colors'),
      colorPalette: document.getElementById('mds-color-palette'),
      colorSearch: document.getElementById('mds-color-search'),
      productColors: document.getElementById('mds-product-colors'),
      // Mobile background controls
      mobileBackgroundControls: document.getElementById('mds-image-background-controls'),
      mobileBackgroundList: document.getElementById('mds-image-background-list'),
      
      // Upload options
      uploadFile: document.getElementById('mds-upload-file'),
      uploadCamera: document.getElementById('mds-upload-camera'),
      uploadLibrary: document.getElementById('mds-upload-library'),
      
      // Help button
      helpBtn: document.getElementById('mds-help-btn'),
      
      // Product image & wrapper
      productImage: document.getElementById('product-image'),
      productWrapper: document.getElementById('product-wrapper'),
      
      // Design overlay container (for showing uploaded designs)
      designOverlay: null, // Will be created dynamically
      
      // Main page elements to sync with
      mainColorSelectors: document.getElementById('color-selectors-container'),
      mainUploadAreas: document.getElementById('upload-areas-container'),
      thumbnailGallery: document.getElementById('thumbnail-gallery'),
      sizeGrid: document.getElementById('size-grid')
    };
    
    // Create design overlay if it doesn't exist
    createDesignOverlay();

    // Re-parent the slide-up panels and backdrop directly under <body> so
    // `position: fixed` is relative to the viewport, not trapped inside an
    // ancestor that creates a containing block (transform/filter/will-change).
    // Without this the panels render as a small box where their original
    // parent sits instead of as a full-width bottom sheet.
    reparentPanelsToBody();
  }

  function reparentPanelsToBody() {
    if (!document.body) return;
    const ids = [
      'mds-panel-backdrop',
      'mds-upload-panel',
      'mds-text-panel',
      'mds-colors-panel',
      'mds-product-colors-panel'
    ];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    });
    // Refresh cached references after the move.
    elements.panelBackdrop = document.getElementById('mds-panel-backdrop');
    elements.uploadPanel = document.getElementById('mds-upload-panel');
    elements.textPanel = document.getElementById('mds-text-panel');
    elements.colorsPanel = document.getElementById('mds-colors-panel');
    elements.productColorsPanel = document.getElementById('mds-product-colors-panel');
  }

  // ============================================
  // DESIGN OVERLAY (Shows uploaded designs on mockup)
  // ============================================
  function createDesignOverlay() {
    if (document.getElementById('mds-design-overlay')) {
      elements.designOverlay = document.getElementById('mds-design-overlay');
      return;
    }
    
    const overlay = document.createElement('div');
    overlay.id = 'mds-design-overlay';
    overlay.className = 'mds-design-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 40%;
      height: 40%;
      pointer-events: none;
      z-index: 50;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    // Add click handler to edit design
    overlay.addEventListener('click', (e) => {
      e.stopPropagation();
      if (mdsState.hasDesign) {
        openPanel('logo-colors');
      }
    });
    // IMPORTANT: Do not intercept pointer events here; let real design overlays handle drag/resize
    overlay.style.pointerEvents = 'none';
    overlay.style.cursor = 'default';
    
    elements.productWrapper?.appendChild(overlay);
    elements.designOverlay = overlay;
  }
  
  function updateDesignOverlay(imageUrl, side) {
    if (!elements.designOverlay) createDesignOverlay();
    
    // The real design image is rendered via the main print-area overlay system.
    // On mobile we keep this overlay primarily for text layers and the design badge.
    // Avoid drawing a second logo image here to prevent the appearance of "two uploads".
    
    if (side === mdsState.currentSide && imageUrl) {
      // Ensure overlay container is visible so any text layers can show
      elements.designOverlay.style.display = 'flex';
      // Do not inject another <img> here; the clipped overlay is the source of truth.
      showDesignBadge();
    }
  }
  
  function showDesignForCurrentSide() {
    // Get designs array for current side (now stored as array per side)
    const sideDesigns = mdsState.uploadedDesigns[mdsState.currentSide];
    
    // Check if there are any designs for this side
    let hasDesigns = false;
    if (Array.isArray(sideDesigns)) {
      hasDesigns = sideDesigns.some(d => d);
    } else if (sideDesigns) {
      // Legacy: single design stored directly
      hasDesigns = true;
    }
    
    if (elements.designOverlay) {
      if (hasDesigns) {
        // Keep container visible so text layers / badge can show
        elements.designOverlay.style.display = 'flex';
      } else {
        // Hide container when no design on this side
        elements.designOverlay.style.display = 'none';
      }
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  function isMobileView() {
    return window.innerWidth <= MDS_CONFIG.breakpoint;
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Detect all available sides from multiple sources
  function detectAvailableSides() {
    const sides = new Set();
    
    // Source 1: ProductConfig mockup_images (most reliable)
    const config = window.ProductConfig || window.productConfig;
    const mockups = config?.mockup_images || config?.mockupImages;
    if (mockups && Object.keys(mockups).length > 0) {
      // Get first color's sides
      const firstColorKey = Object.keys(mockups)[0];
      const colorMockups = mockups[firstColorKey];
      if (colorMockups && typeof colorMockups === 'object') {
        Object.keys(colorMockups).forEach(side => {
          if (side && colorMockups[side]) {
            sides.add(side.toLowerCase());
          }
        });
      }
    }
    
    // Source 2: Configurator data upload areas
    const uploadAreas = window.configuratorData?.upload_areas || [];
    uploadAreas.forEach(area => {
      const name = area.area_name?.toLowerCase();
      if (name) sides.add(name);
    });
    
    // Source 3: Thumbnail gallery
    const thumbItems = document.querySelectorAll('.thumbnail-item img');
    thumbItems.forEach(img => {
      const classList = Array.from(img.classList);
      classList.forEach(cls => {
        if (cls.endsWith('-thumbnail')) {
          const side = cls.replace('-thumbnail', '');
          if (side && side !== 'featured') sides.add(side);
        }
      });
    });
    
    // Source 4: Look at image metafields structure
    const thumbContainer = document.querySelector('.thumbnail-item');
    if (thumbContainer) {
      ['front', 'back', 'left', 'right', 'left-side', 'right-side'].forEach(side => {
        const sideClass = side.replace('-side', '');
        const sideImg = thumbContainer.querySelector(`.${sideClass}-thumbnail, .${side}-thumbnail`);
        if (sideImg) sides.add(sideClass);
      });
    }
    
    // Source 5: Check product wrapper data attributes
    const productData = document.getElementById('product-data');
    if (productData?.dataset.sides) {
      try {
        const dataSides = JSON.parse(productData.dataset.sides);
        dataSides.forEach(s => sides.add(s.toLowerCase()));
      } catch (e) {}
    }
    
    // Source 6: Check configuratorData.sides array
    if (window.configuratorData?.sides && Array.isArray(window.configuratorData.sides)) {
      window.configuratorData.sides.forEach(side => {
        if (side && side.name) {
          sides.add(side.name.toLowerCase());
        }
      });
    }
    
    // Fallback to default sides if nothing found
    if (sides.size === 0) {
      MDS_CONFIG.defaultSides.forEach(s => sides.add(s));
    }
    
    // Normalize side names (remove -side suffix for consistency)
    const normalizedSides = new Set();
    sides.forEach(side => {
      const normalized = side.replace('-side', '');
      normalizedSides.add(normalized);
    });

    // Restrict to sides that actually have a configured print area, when
    // the configuration is loaded. If print_areas isn't loaded yet, leave
    // the detected set untouched so the side nav can render something.
    try {
      const cfg = window.productConfig || window.ProductConfig;
      if (cfg && Array.isArray(cfg.print_areas) && cfg.print_areas.length > 0) {
        const configured = new Set();
        cfg.print_areas.forEach(area => {
          if (!area) return;
          const id = String(area.id || area.area_name || '').trim().toLowerCase();
          if (id) configured.add(id.replace('-side', ''));
        });
        if (configured.size > 0) {
          Array.from(normalizedSides).forEach(side => {
            if (!configured.has(side)) normalizedSides.delete(side);
          });
        }
      }
    } catch (_) {}

    // Convert to array and sort (front first, then back, then others)
    const sideOrder = ['front', 'back', 'left', 'right'];
    const sortedSides = Array.from(normalizedSides).sort((a, b) => {
      const aIdx = sideOrder.indexOf(a);
      const bIdx = sideOrder.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });

    console.log('📱 Detected sides:', sortedSides);
    return sortedSides;
  }

  // ============================================
  // VISIBILITY CONTROL
  // ============================================
  function updateMobileVisibility() {
    const wasMobile = mdsState.isMobile;
    mdsState.isMobile = isMobileView();
    
    if (mdsState.isMobile === wasMobile) return;
    
    const mobileElements = [
      elements.quickBar,
      elements.floatingActions,
      elements.sideNav,
      elements.bottomNav,
      elements.helpBtn
    ];
    
    mobileElements.forEach(el => {
      if (el) {
        if (mdsState.isMobile) {
          el.classList.remove('hidden');
          el.classList.add('flex');
        } else {
          el.classList.add('hidden');
          el.classList.remove('flex');
        }
      }
    });
    
    // Close any open panels when switching to desktop
    if (!mdsState.isMobile && mdsState.activePanel) {
      closeAllPanels();
    }
    
    console.log('📱 Mobile Design Studio:', mdsState.isMobile ? 'ACTIVE' : 'INACTIVE');
  }

  // ============================================
  // PANEL MANAGEMENT
  // ============================================
  function cancelPendingPanelTimers() {
    if (Array.isArray(mdsState._pendingPanelTimers)) {
      mdsState._pendingPanelTimers.forEach(id => clearTimeout(id));
    }
    mdsState._pendingPanelTimers = [];
  }

  function openPanel(panelId) {
    const panelMap = {
      'upload': elements.uploadPanel,
      'text': elements.textPanel,
      'logo-colors': elements.colorsPanel,
      'colors': elements.productColorsPanel
    };

    let panel = panelMap[panelId];
    // Re-resolve from DOM in case cacheElements ran before the element existed
    if (!panel) {
      const idMap = {
        'upload': 'mds-upload-panel',
        'text': 'mds-text-panel',
        'logo-colors': 'mds-colors-panel',
        'colors': 'mds-product-colors-panel'
      };
      panel = document.getElementById(idMap[panelId]);
      if (panel) {
        if (panelId === 'upload') elements.uploadPanel = panel;
        else if (panelId === 'text') elements.textPanel = panel;
        else if (panelId === 'logo-colors') elements.colorsPanel = panel;
        else if (panelId === 'colors') elements.productColorsPanel = panel;
      }
    }
    if (!panel) {
      console.warn('📱 openPanel: panel not found for', panelId);
      return;
    }

    if (panelId === 'logo-colors') {
      mountPantoneLibraryForMobile();
    }

    // Close other panels (sync) but cancel any pending hide timers so they
    // don't fire on the panel we're about to open.
    closeOtherPanels(panel);

    if (!elements.panelBackdrop) {
      elements.panelBackdrop = document.getElementById('mds-panel-backdrop');
    }
    if (elements.panelBackdrop) {
      elements.panelBackdrop.classList.remove('hidden');
      elements.panelBackdrop.classList.add('flex');
      // Force-on inline styles defeat any stale `.hidden`/cascade race.
      elements.panelBackdrop.style.display = 'flex';
      elements.panelBackdrop.style.visibility = 'visible';
      elements.panelBackdrop.style.opacity = '1';
      elements.panelBackdrop.style.pointerEvents = 'auto';
      requestAnimationFrame(() => {
        elements.panelBackdrop.classList.add('open');
      });
    }

    panel.classList.remove('hidden');
    panel.classList.add('flex');
    // Belt-and-suspenders: ensure the panel is rendered and on-screen even
    // if the cascade or a stray `.hidden` class would otherwise hide it.
    panel.style.display = 'flex';
    panel.style.visibility = 'visible';
    panel.style.pointerEvents = 'auto';
    panel.style.zIndex = '10000';
    requestAnimationFrame(() => {
      panel.classList.add('open');
      panel.style.transform = 'translateY(0)';
    });

    updateNavActiveState(panelId);
    mdsState.activePanel = panelId;
    document.body.style.overflow = 'hidden';

    console.log('📱 Panel opened:', panelId);
  }

  function closeOtherPanels(keepPanel) {
    cancelPendingPanelTimers();
    const panels = [
      elements.uploadPanel,
      elements.textPanel,
      elements.colorsPanel,
      elements.productColorsPanel
    ];
    panels.forEach(panel => {
      if (!panel || panel === keepPanel) return;
      panel.classList.remove('open');
      panel.style.transform = '';
      const id = setTimeout(() => {
        panel.classList.add('hidden');
        panel.classList.remove('flex');
        panel.style.display = '';
        panel.style.visibility = '';
        panel.style.pointerEvents = '';
      }, MDS_CONFIG.panelAnimationDuration);
      mdsState._pendingPanelTimers.push(id);
    });
  }

  function closeAllPanels() {
    cancelPendingPanelTimers();
    const panels = [
      elements.uploadPanel,
      elements.textPanel,
      elements.colorsPanel,
      elements.productColorsPanel
    ];

    panels.forEach(panel => {
      if (panel) {
        panel.classList.remove('open');
        panel.style.transform = '';
        const id = setTimeout(() => {
          panel.classList.add('hidden');
          panel.classList.remove('flex');
          panel.style.display = '';
          panel.style.visibility = '';
          panel.style.pointerEvents = '';
        }, MDS_CONFIG.panelAnimationDuration);
        mdsState._pendingPanelTimers.push(id);
      }
    });

    if (elements.panelBackdrop) {
      elements.panelBackdrop.classList.remove('open');
      elements.panelBackdrop.style.opacity = '';
      const id = setTimeout(() => {
        elements.panelBackdrop.classList.add('hidden');
        elements.panelBackdrop.classList.remove('flex');
        elements.panelBackdrop.style.display = '';
        elements.panelBackdrop.style.visibility = '';
        elements.panelBackdrop.style.pointerEvents = '';
      }, MDS_CONFIG.panelAnimationDuration);
      mdsState._pendingPanelTimers.push(id);
    }

    if (typeof hideInlineLibrary === 'function') {
      hideInlineLibrary();
    }

    mdsState.activePanel = null;
    document.body.style.overflow = '';
  }

  function updateNavActiveState(activePanel) {
    if (!elements.navItems) return;
    
    elements.navItems.forEach(item => {
      const panel = item.dataset.panel;
      if (panel === activePanel) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }

  // ============================================
  // SIDE NAVIGATION (View Switcher)
  // ============================================
  function initializeSideNav() {
    // Detect all available sides from multiple sources
    mdsState.availableSides = detectAvailableSides();
    
    if (!elements.sideNav) return;

    // Avoid rebuilding the side nav if nothing material has changed.
    // This prevents duplicate event handlers and reduces "race-like" repeated init.
    const currentColor = window.selectedColor || '';
    const sideNavSignature = `${currentColor}|${(mdsState.availableSides || []).join(',')}`;

    // Check: skip if signature matches AND we have buttons already (already built)
    if (mdsState._sideNavSignature === sideNavSignature && elements.sideNav.children.length > 0) {
      return;
    }
    mdsState._sideNavSignature = sideNavSignature;
    
    // Clear existing
    elements.sideNav.innerHTML = '';
    
    // Get current color for thumbnails
    
    console.log('📱 Building side nav with sides:', mdsState.availableSides);
    
    // Create side thumbnails for ALL detected sides
    mdsState.availableSides.forEach((side, index) => {
      const thumb = document.createElement('button');
      thumb.className = `mds-side-thumb ${index === 0 ? 'active' : ''}`;
      thumb.dataset.side = side;
      thumb.setAttribute('aria-label', `${side} view`);
      
      // Try to get thumbnail image
      const thumbImg = getThumbImageForSide(side, currentColor);
      
      // Create label
      const sideLabel = side.charAt(0).toUpperCase() + side.slice(1);
      
      thumb.innerHTML = `
        <img src="${thumbImg}" alt="${sideLabel} view" onerror="this.parentElement.innerHTML='<span style=\\'display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#f1f5f9;font-size:11px;font-weight:600;color:#64748b;\\'>${sideLabel.charAt(0)}</span>'">
      `;
      
      thumb.addEventListener('click', () => switchSide(side));
      elements.sideNav.appendChild(thumb);
    });
    
    // Also populate side tabs in panels
    populateSideTabs(elements.uploadSideTabs);
    populateSideTabs(elements.textSideTabs);
  }

  function getThumbImageForSide(side, color) {
    const sideLower = side.toLowerCase();

    // Try 0: Use ProductConfig.mockup_images when available (most accurate)
    try {
      const config = window.ProductConfig || window.productConfig;
      const mockups = config?.mockup_images || config?.mockupImages;
      if (mockups && Object.keys(mockups).length > 0) {
        const requestedColor = color || window.selectedColor || window.currentSelectedColor || '';
        const mockupKeys = Object.keys(mockups);
        
        // Try exact match first, then partial/normalized match
        let colorKey = mockupKeys.find(k => k === requestedColor);
        if (!colorKey) {
          // Try case-insensitive match
          const lowerColor = requestedColor.toLowerCase().replace(/\s+/g, '');
          colorKey = mockupKeys.find(k => k.toLowerCase().replace(/\s+/g, '') === lowerColor);
        }
        if (!colorKey) {
          // Try partial match (color name contains or is contained by key)
          const lowerColor = requestedColor.toLowerCase();
          colorKey = mockupKeys.find(k => 
            k.toLowerCase().includes(lowerColor) || lowerColor.includes(k.toLowerCase())
          );
        }
        if (!colorKey) {
          // Fallback to first available
          colorKey = mockupKeys[0];
        }
        
        const byColor = mockups[colorKey];
        const sideValue = byColor ? byColor[sideLower] : null;

        // A mockup_images entry is EITHER a plain URL string OR an enriched object
        // ({ url, shopify_image_id, offset_x, offset_y, ... }) written by the configurator's
        // image sync. Returning the object verbatim stringified to "[object Object]" in the
        // img src, which the browser resolved to /products/[object Object] -> 404.
        const resolvedUrl = typeof sideValue === 'string'
          ? sideValue
          : (sideValue && typeof sideValue.url === 'string' ? sideValue.url : null);

        if (resolvedUrl) {
          console.log('📱 Using config mockup image for', sideLower, 'color:', colorKey, '→', resolvedUrl);
          return resolvedUrl;
        }
      }
    } catch (_) {}

    // Try 0.5: Look at current color image arrays that configurator-integration maintains.
    const colorSlug = String(color || window.selectedColor || window.currentSelectedColor || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    const imagePools = []
      .concat(Array.isArray(window.currentColorImages) ? window.currentColorImages : [])
      .concat(Array.isArray(window.productImages) ? window.productImages : []);
    if (imagePools.length > 0) {
      const byAltMatch = imagePools.find((img) => {
        const alt = String(img?.alt || '').toLowerCase();
        if (!alt) return false;
        const sideMatch = alt.endsWith(`-${sideLower}`);
        if (!sideMatch) return false;
        if (!colorSlug) return true;
        return alt.includes(`mockup-${colorSlug}-`);
      });
      if (byAltMatch?.src) {
        return byAltMatch.src;
      }
    }

    // Try multiple selectors to find thumbnail in the DOM as fallback
    const colorHex = (color || '').replace('#', '').toUpperCase();
    console.log('📱 Looking for thumbnail in DOM:', sideLower, 'color:', colorHex);

    // Try 1: Color-specific thumbnail with exact class match
    if (colorHex) {
      // Look in visible thumbnail-item
      const colorContainer = document.querySelector(`.thumbnail-item.color-${colorHex}:not(.hidden)`);
      if (colorContainer) {
        const sideImg = colorContainer.querySelector(`img.${sideLower}-thumbnail`);
        if (sideImg?.src) {
          console.log('📱 Found color-specific thumbnail:', sideImg.src);
          return sideImg.src;
        }
      }
    }
    
    // Try 2: Look in active/visible thumbnail container
    const activeContainer = document.querySelector('.thumbnail-item:not(.hidden), .thumbnail-item.active');
    if (activeContainer) {
      const sideImg = activeContainer.querySelector(`img.${sideLower}-thumbnail`);
      if (sideImg?.src) {
        console.log('📱 Found active container thumbnail:', sideImg.src);
        return sideImg.src;
      }
    }
    
    // Try 3: Search all thumbnail items for this side
    const allThumbs = document.querySelectorAll(`.thumbnail-item img.${sideLower}-thumbnail`);
    if (allThumbs.length > 0) {
      // Prefer non-hidden ones
      for (const thumb of allThumbs) {
        if (!thumb.closest('.hidden') && thumb.src) {
          console.log('📱 Found any thumbnail:', thumb.src);
          return thumb.src;
        }
      }
      // Fallback to first one
      if (allThumbs[0]?.src) return allThumbs[0].src;
    }
    
    // Try 4: Look for data attributes
    const dataThumb = document.querySelector(`[data-side="${sideLower}"] img, [data-view="${sideLower}"] img`);
    if (dataThumb?.src) return dataThumb.src;
    
    // Fallback to current product image (but this might be wrong side)
    console.log('📱 No thumbnail found for side:', sideLower);
    return elements.productImage?.src || '';
  }

  function switchSide(side) {
    const prevSide = mdsState.currentSide;
    mdsState.currentSide = side;
    
    // Update side nav active state
    const thumbs = elements.sideNav?.querySelectorAll('.mds-side-thumb');
    thumbs?.forEach(thumb => {
      thumb.classList.toggle('active', thumb.dataset.side === side);
    });
    
    // Update side tabs in panels
    updateSideTabsActive(side);
    
    // Update product image
    const currentColor = window.selectedColor || '';
    const thumbImg = getThumbImageForSide(side, currentColor);
    
    // Update main product image
    if (elements.productImage && thumbImg) {
      elements.productImage.src = thumbImg;
    }
    
    // Trigger main page side switch (if available)
    if (typeof window.handleThumbnailClick === 'function') {
      window.handleThumbnailClick(side, thumbImg, 0);
    }
    
    // Update print area overlay to match new side
    if (typeof window.updatePrintSideOverlay === 'function') {
      setTimeout(() => window.updatePrintSideOverlay(side), 100);
    }
    
    // Show design for current side
    showDesignForCurrentSide();
    
    // Update floating actions visibility based on design state
    updateFloatingActionsVisibility();
    
    console.log('📱 Switched from', prevSide, 'to', side);
  }

  function populateSideTabs(container) {
    if (!container) return;
    
    container.innerHTML = '';
    
    mdsState.availableSides.forEach((side, index) => {
      const tab = document.createElement('button');
      tab.className = `mds-side-tab ${side === mdsState.currentSide ? 'active' : ''}`;
      tab.dataset.side = side;
      tab.textContent = side.charAt(0).toUpperCase() + side.slice(1);
      
      tab.addEventListener('click', () => {
        switchSide(side);
        // Update tab active state within this container
        container.querySelectorAll('.mds-side-tab').forEach(t => {
          t.classList.toggle('active', t.dataset.side === side);
        });
      });
      
      container.appendChild(tab);
    });
  }

  function updateSideTabsActive(side) {
    [elements.uploadSideTabs, elements.textSideTabs].forEach(container => {
      if (!container) return;
      container.querySelectorAll('.mds-side-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.side === side);
      });
    });
  }
  
  function updateFloatingActionsVisibility() {
    // Check if current side has any designs from multiple sources:
    // 1. MDS internal state (uploadedDesigns)
    // 2. DOM overlays (design-overlay-wrapper elements)
    // 3. Global configurator state
    // 4. Actual overlay images in the DOM
    // 5. UnifiedStateManager (for restoration)
    const currentSide = mdsState.currentSide || 'front';
    let hasDesignOnCurrentSide = false;
    
    // Source 1: MDS internal state
    const sideDesigns = mdsState.uploadedDesigns[currentSide];
    if (Array.isArray(sideDesigns)) {
      hasDesignOnCurrentSide = sideDesigns.some(d => d);
    } else if (sideDesigns) {
      hasDesignOnCurrentSide = true;
    }
    
    // Source 2: Check DOM for actual design overlays on this side (multiple selector patterns)
    if (!hasDesignOnCurrentSide) {
      const overlaySelectors = [
        `[id^="design-overlay-"][data-side="${currentSide}"]`,
        `[id^="overlay-wrapper-"][data-side="${currentSide}"]`,
        `[id^="design-overlay-${currentSide}"]`,
        `[id^="overlay-wrapper-${currentSide}"]`,
        `.design-overlay[data-side="${currentSide}"]`,
        `#print-area-${currentSide} img`,
        `#overlay-wrapper-${currentSide}-0 img`,
        `#overlay-wrapper-${currentSide}-1 img`
      ];
      
      for (const selector of overlaySelectors) {
        const overlays = document.querySelectorAll(selector);
        overlays.forEach(overlay => {
          // Check if it's an img element with a valid src
          if (overlay.tagName === 'IMG') {
            if (overlay.src && !overlay.src.includes('placeholder') && !overlay.src.includes('data:image/gif')) {
              hasDesignOnCurrentSide = true;
            }
          } else {
            // Check for img inside the overlay
            const img = overlay.querySelector('img');
            if (img && img.src && !img.src.includes('placeholder') && !img.src.includes('data:image/gif')) {
              hasDesignOnCurrentSide = true;
            }
          }
        });
        if (hasDesignOnCurrentSide) break;
      }
    }
    
    // Source 3: Check global configurator state
    if (!hasDesignOnCurrentSide) {
      const stateModule = window.ProductConfigurator?.State || window.State;
      if (stateModule?.uploadedImages) {
        const sideImages = stateModule.uploadedImages[currentSide];
        if (Array.isArray(sideImages)) {
          hasDesignOnCurrentSide = sideImages.some(img => img && img.url);
        } else if (sideImages && sideImages.url) {
          hasDesignOnCurrentSide = true;
        }
      }
    }
    
    // Source 4: Check UnifiedStateManager (for restoration scenarios)
    if (!hasDesignOnCurrentSide && window.UnifiedStateManager) {
      try {
        const usm = window.UnifiedStateManager;
        const areaState = usm.getAreaState?.(currentSide);
        if (areaState?.images?.length > 0) {
          hasDesignOnCurrentSide = areaState.images.some(img => img && (img.url || img.originalUrl));
        }
      } catch (e) {
        // Ignore errors
      }
    }
    
    // Source 5: Check for visible overlay wrappers with actual images (most reliable for restoration)
    if (!hasDesignOnCurrentSide) {
      const visibleWrappers = document.querySelectorAll(`[id^="overlay-wrapper-${currentSide}-"]`);
      visibleWrappers.forEach(wrapper => {
        if (wrapper.style.display !== 'none' && wrapper.style.visibility !== 'hidden') {
          const img = wrapper.querySelector('img');
          if (img && img.src && img.src.length > 50) { // Base64 images are long
            hasDesignOnCurrentSide = true;
          }
        }
      });
    }
    
    // Source 6: Check local design cache (for cross-device immediate feedback)
    if (!hasDesignOnCurrentSide && window.designCacheManager) {
      try {
        const productId = window.productConfig?.product_id;
        const variantId = window.currentVariantId;
        const cartItemKey = new URLSearchParams(window.location.search).get('edit_cart_item');
        
        const cacheInfo = window.designCacheManager.getQuickInfo({
          cartItemKey: cartItemKey,
          productId: productId,
          variantId: variantId
        });
        
        if (cacheInfo && cacheInfo.hasDesigns && cacheInfo.sidesWithDesigns?.includes(currentSide)) {
          hasDesignOnCurrentSide = true;
          console.log(`📱 Design found in cache for ${currentSide}`);
        }
      } catch (e) {
        // Ignore cache errors
      }
    }
    
    // Update floating actions visibility (with fallback DOM query)
    const floatingActions = elements.floatingActions || document.getElementById('mds-floating-actions');
    if (floatingActions) {
      if (hasDesignOnCurrentSide) {
        floatingActions.classList.add('has-design');
        console.log(`📱 Floating actions HIDDEN (has-design class added)`);
      } else {
        floatingActions.classList.remove('has-design');
        console.log(`📱 Floating actions VISIBLE (has-design class removed)`);
      }
      // Cache the element if it wasn't cached before
      if (!elements.floatingActions) {
        elements.floatingActions = floatingActions;
      }
    } else {
      console.warn('📱 Floating actions element not found');
    }
    
    console.log(`📱 Floating actions visibility check: side=${currentSide}, hasDesign=${hasDesignOnCurrentSide}`);
  }

  // ============================================
  // UPLOAD FUNCTIONALITY
  // ============================================
  function handleUploadClick() {
    console.log('📱 handleUploadClick fired — opening upload panel');
    openPanel('upload');
  }

  function handleUploadFile() {
    // Create hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.svg,.ai,.eps,.pdf';
    input.style.display = 'none';
    
    input.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) {
        processUploadedFile(file);
      }
      input.remove();
    });
    
    document.body.appendChild(input);
    input.click();
    closeAllPanels();
  }

  function handleUploadCamera() {
    // Mobile camera capture
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';
    
    input.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) {
        processUploadedFile(file);
      }
      input.remove();
    });
    
    document.body.appendChild(input);
    input.click();
    closeAllPanels();
  }

  // ============================================
  // INLINE LIBRARY STATE
  // ============================================
  const inlineLibraryState = {
    isOpen: false,
    images: [],
    selectedImage: null,
    currentSide: 'front',
    currentIndex: 0,
    selectionToken: null
  };

  function getLibraryService() {
    return window.LibraryImageService || null;
  }

  function handleUploadLibrary() {
    // Get pending upload info
    const pendingUpload = mdsState.pendingMobileUpload;
    inlineLibraryState.currentSide = pendingUpload?.side || mdsState.currentSide;
    inlineLibraryState.currentIndex = pendingUpload?.index || 0;
    inlineLibraryState.selectedImage = null;
    
    console.log(`📱 Opening inline library for ${inlineLibraryState.currentSide}[${inlineLibraryState.currentIndex}]`);
    
    // Show inline library view, hide upload options
    showInlineLibrary();
    
    // Load images
    loadInlineLibraryImages();
  }

  function showInlineLibrary() {
    const uploadOptions = document.querySelector('.mds-upload-options');
    const uploadTip = document.getElementById('mds-upload-tip');
    const sideTabs = document.getElementById('mds-upload-side-tabs');
    const inlineLibrary = document.getElementById('mds-inline-library');
    
    if (uploadOptions) uploadOptions.style.display = 'none';
    if (uploadTip) uploadTip.style.display = 'none';
    if (sideTabs) sideTabs.style.display = 'none';
    if (inlineLibrary) inlineLibrary.style.display = 'flex';
    
    inlineLibraryState.isOpen = true;
  }

  function hideInlineLibrary() {
    const uploadOptions = document.querySelector('.mds-upload-options');
    const uploadTip = document.getElementById('mds-upload-tip');
    const sideTabs = document.getElementById('mds-upload-side-tabs');
    const inlineLibrary = document.getElementById('mds-inline-library');
    
    if (uploadOptions) uploadOptions.style.display = 'flex';
    if (uploadTip) uploadTip.style.display = 'block';
    if (sideTabs) sideTabs.style.display = 'flex';
    if (inlineLibrary) inlineLibrary.style.display = 'none';
    
    inlineLibraryState.isOpen = false;
    inlineLibraryState.selectedImage = null;
    updateInlineLibraryUseButton();
  }

  async function loadInlineLibraryImages() {
    const loadingEl = document.getElementById('mds-inline-library-loading');
    const loginEl = document.getElementById('mds-inline-library-login');
    const emptyEl = document.getElementById('mds-inline-library-empty');
    const gridEl = document.getElementById('mds-inline-library-grid');
    const footerEl = document.getElementById('mds-inline-library-footer');
    
    // Show loading
    if (loadingEl) loadingEl.style.display = 'flex';
    if (loginEl) loginEl.style.display = 'none';
    if (emptyEl) emptyEl.style.display = 'none';
    if (gridEl) gridEl.style.display = 'none';
    if (footerEl) footerEl.style.display = 'none';
    
    try {
      const libraryService = getLibraryService();
      const data = libraryService
        ? await libraryService.fetchImages()
        : { success: false, images: [], requires_login: true };
      
      if (loadingEl) loadingEl.style.display = 'none';
      
      if (data.requires_login) {
        if (loginEl) loginEl.style.display = 'flex';
        return;
      }
      
      if (data.success && data.images && data.images.length > 0) {
        inlineLibraryState.images = data.images;
        renderInlineLibraryGrid();
        if (gridEl) gridEl.style.display = 'grid';
        if (footerEl) footerEl.style.display = 'block';
      } else {
        if (emptyEl) emptyEl.style.display = 'flex';
      }
      
    } catch (error) {
      console.error('📱 Failed to load library images:', error);
      if (loadingEl) loadingEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'flex';
    }
  }

  function renderInlineLibraryGrid() {
    const gridEl = document.getElementById('mds-inline-library-grid');
    if (!gridEl) return;
    const libraryService = getLibraryService();

    const normalizeUrl = (url) => libraryService ? libraryService.normalizeStorageUrl(url) : url;
    const buildUrlCandidates = (url) => {
      if (libraryService && typeof libraryService.buildStorageUrlCandidates === 'function') {
        return libraryService.buildStorageUrlCandidates(url);
      }
      const normalized = normalizeUrl(url);
      return normalized ? [normalized] : [];
    };
    const withThumbParam = (url, width) => {
      if (!url || typeof url !== 'string') return url;
      if (!/\/apps\/configurator\/storage\//.test(url) && !/\/storage\//.test(url)) return url;
      if (/[?&]w=\d+/.test(url)) return url;
      return url + (url.includes('?') ? '&' : '?') + 'w=' + width;
    };
    
    gridEl.innerHTML = inlineLibraryState.images.map((img, i) => {
      const mainCandidates = buildUrlCandidates(img.url);
      const safeUrl = mainCandidates[0] || normalizeUrl(img.url);
      const thumbCandidates = buildUrlCandidates(img.thumbnail || img.url).map((u) => withThumbParam(u, 300));
      const safeThumb = thumbCandidates[0] || withThumbParam(normalizeUrl(img.thumbnail || img.url), 300);
      const fallbackAttr = thumbCandidates.join('|');
      const mainAttr = mainCandidates.join('|');
      const isPaid = Boolean(img.isPaid || img.is_paid || String(img.paymentStatus || img.payment_status || '').toLowerCase() === 'paid');
      const paymentStatus = String(img.paymentStatus || img.payment_status || (isPaid ? 'paid' : 'pending')).toLowerCase();
      const mediaFingerprint = img.mediaFingerprint || img.media_fingerprint || '';
      const badgeHtml = isPaid ? '<span class="mds-inline-library-card-badge">Paid</span>' : '';
      const loadingAttr = i < 6 ? 'eager' : 'lazy';
      const priorityAttr = i < 6 ? 'high' : 'low';
      return `
      <div class="mds-inline-library-card" data-image-id="${img.id}" data-image-url="${safeUrl}" data-image-url-candidates="${mainAttr}" data-media-fingerprint="${mediaFingerprint}" data-payment-status="${paymentStatus}" data-is-paid="${isPaid ? 'true' : 'false'}">
        <img src="${safeThumb}" alt="${img.filename || 'Design'}" data-fallback-srcs="${fallbackAttr}" data-main-srcs="${mainAttr}" data-fallback-index="0" loading="${loadingAttr}" decoding="async" fetchpriority="${priorityAttr}" width="300" height="300">
        ${badgeHtml}
        <div class="mds-inline-library-card-check">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
      </div>
    `;
    }).join('');
    
    // Bind click events
    gridEl.querySelectorAll('.mds-inline-library-card').forEach(card => {
      card.addEventListener('click', () => selectInlineLibraryImage(card));
    });

    // Retry thumbnail using alternate storage directories when first URL 404s.
    gridEl.querySelectorAll('.mds-inline-library-card img[data-fallback-srcs]').forEach((imgEl) => {
      imgEl.addEventListener('error', function onThumbError() {
        const all = String(this.dataset.fallbackSrcs || '')
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean);
        if (!all.length) return;
        const current = Number.parseInt(this.dataset.fallbackIndex || '0', 10) || 0;
        const next = current + 1;
        if (next >= all.length) {
          this.removeEventListener('error', onThumbError);
          const card = this.closest('.mds-inline-library-card');
          if (card) {
            card.classList.add('mds-inline-library-card--broken');
            card.dataset.imageUrl = '';
            card.setAttribute('aria-disabled', 'true');
          }
          return;
        }
        this.dataset.fallbackIndex = String(next);
        this.src = all[next];
        const card = this.closest('.mds-inline-library-card');
        if (card) {
          const mainList = String(this.dataset.mainSrcs || '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
          if (mainList[next]) {
            card.dataset.imageUrl = mainList[next];
          } else {
            card.dataset.imageUrl = all[next].replace(/([?&])w=\d+(&|$)/, '$1').replace(/[?&]$/, '');
          }
        }
      });
    });
  }

  function selectInlineLibraryImage(card) {
    if (!card || card.classList.contains('mds-inline-library-card--broken')) return;
    const resolvedUrl = String(card.dataset.imageUrl || '').trim();
    if (!resolvedUrl) return;

    // Deselect all
    document.querySelectorAll('.mds-inline-library-card').forEach(c => c.classList.remove('selected'));
    
    // Select this one
    card.classList.add('selected');
    
    inlineLibraryState.selectedImage = {
      id: card.dataset.imageId,
      url: resolvedUrl,
      filename: card.querySelector('img')?.alt || 'design.png',
      mediaFingerprint: card.dataset.mediaFingerprint || '',
      paymentStatus: card.dataset.paymentStatus || 'pending',
      isPaid: card.dataset.isPaid === 'true',
      badge: card.dataset.isPaid === 'true' ? 'Paid' : '',
      isFree: card.dataset.isPaid === 'true'
    };
    
    updateInlineLibraryUseButton();
  }

  function updateInlineLibraryUseButton() {
    const btn = document.getElementById('mds-inline-library-use-btn');
    if (btn) {
      btn.disabled = !inlineLibraryState.selectedImage;
    }
  }

  async function useSelectedLibraryImage() {
    if (!inlineLibraryState.selectedImage) return;
    
    console.log('📱 Using selected library image:', inlineLibraryState.selectedImage);
    
    const { url, id, filename } = inlineLibraryState.selectedImage;
    const side = inlineLibraryState.currentSide;
    const index = inlineLibraryState.currentIndex;
    
    const libraryService = getLibraryService();
    if (libraryService && inlineLibraryState.selectionToken) {
      await libraryService.applySelection(inlineLibraryState.selectionToken, {
        id,
        url,
        filename,
        mediaFingerprint: inlineLibraryState.selectedImage.mediaFingerprint,
        paymentStatus: inlineLibraryState.selectedImage.paymentStatus,
        isPaid: inlineLibraryState.selectedImage.isPaid,
        badge: inlineLibraryState.selectedImage.badge
      });
    }
    
    // Process the image using the existing flow
    if (typeof window.processLibraryImage === 'function') {
      window.processLibraryImage({
        ...inlineLibraryState.selectedImage,
        side: side,
        index: index
      });
    } else {
      // Fallback for older bundles without processLibraryImage binding
      document.dispatchEvent(new CustomEvent('libraryImage:applied', {
        detail: {
          ...inlineLibraryState.selectedImage,
          side,
          index,
          isFree: inlineLibraryState.selectedImage.isPaid === true
        }
      }));
    }
    
    // Close the panel
    hideInlineLibrary();
    closeAllPanels();
  }

  function initInlineLibraryEvents() {
    // Back button
    const backBtn = document.getElementById('mds-inline-library-back');
    if (backBtn) {
      backBtn.addEventListener('click', hideInlineLibrary);
    }
    
    // Use selected button
    const useBtn = document.getElementById('mds-inline-library-use-btn');
    if (useBtn) {
      useBtn.addEventListener('click', useSelectedLibraryImage);
    }

    document.addEventListener('customerMediaLibraryImagesUpdated', (e) => {
      if (!inlineLibraryState.isOpen) return;
      const images = Array.isArray(e.detail?.images) ? e.detail.images : [];

      inlineLibraryState.images = images;
      inlineLibraryState.selectedImage = null;
      updateInlineLibraryUseButton();

      const emptyEl = document.getElementById('mds-inline-library-empty');
      const gridEl = document.getElementById('mds-inline-library-grid');
      const footerEl = document.getElementById('mds-inline-library-footer');
      if (images.length > 0) {
        renderInlineLibraryGrid();
        if (emptyEl) emptyEl.style.display = 'none';
        if (gridEl) gridEl.style.display = 'grid';
        if (footerEl) footerEl.style.display = 'block';
      } else {
        if (emptyEl) emptyEl.style.display = 'flex';
        if (gridEl) gridEl.style.display = 'none';
        if (footerEl) footerEl.style.display = 'none';
      }
    });
  }
  
  function processUploadedFile(file) {
    // Check if we have pending mobile upload info (from "Add Additional Image" flow)
    const pendingUpload = mdsState.pendingMobileUpload;
    const side = pendingUpload?.side || mdsState.currentSide;
    const fileName = file.name.toLowerCase();
    
    // Get the upload index - use pending info if available, otherwise use current count
    let uploadIndex;
    if (pendingUpload && pendingUpload.side === side && typeof pendingUpload.index === 'number') {
      uploadIndex = pendingUpload.index;
      console.log(`📱 Using pending upload index: ${uploadIndex} for side: ${side}`);
    } else {
      // Get the current upload index for this side (per-side indexing)
      if (!mdsState.uploadCounts[side]) {
        mdsState.uploadCounts[side] = 0;
      }
      uploadIndex = mdsState.uploadCounts[side];
    }
    
    // Clear pending upload info
    mdsState.pendingMobileUpload = null;
    
    // Check if it's a vector file that needs server-side processing
    const isVector = ['.eps', '.ai', '.pdf'].some(ext => fileName.endsWith(ext));
    
    console.log('📱 Processing file:', file.name, 'isVector:', isVector, 'side:', side, 'index:', uploadIndex);
    
    if (isVector) {
      // Vector files need server processing - trigger main page upload handler
      console.log('📱 Vector file detected, using main page handler');
      
      // Find the upload input for this side and trigger it with the file
      const uploadInput = findUploadInputForSide(side);
      if (uploadInput) {
        // Create a DataTransfer to set the files property
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        uploadInput.files = dataTransfer.files;
        
        // Trigger change event
        const event = new Event('change', { bubbles: true });
        uploadInput.dispatchEvent(event);
      } else {
        // Fallback: trigger upload via global function
        if (typeof window.handleVectorUpload === 'function') {
          window.handleVectorUpload(file, side, uploadIndex);
        } else if (typeof window.triggerUpload === 'function') {
          alert('Vector files (EPS, AI, PDF) require processing. Please use the main upload button.');
        }
      }
      // Increment upload count for vector files
      mdsState.uploadCounts[side]++;
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('mdsDesignUploaded', {
        detail: { file, side, index: uploadIndex, isVector: true }
      }));
      
      console.log('📱 Vector upload initiated for side:', side, 'at index:', uploadIndex);
    } else {
      // Regular image file - read as data URL and trigger full processing pipeline
      const reader = new FileReader();
      reader.onload = function(e) {
        const dataURL = e.target.result;
        
        // Store in uploadedDesigns per side with index
        if (!mdsState.uploadedDesigns[side]) {
          mdsState.uploadedDesigns[side] = [];
        }
        mdsState.uploadedDesigns[side][uploadIndex] = dataURL;
        
        // Display on mockup immediately
        updateDesignOverlay(dataURL, side);
        
        // Trigger the FULL desktop upload pipeline via handleUpload for complete processing.
        // We create a fake change-event with a DataTransfer so handleUpload can read event.target.files.
        if (typeof window.handleUpload === 'function') {
          console.log('📱 Triggering full desktop upload pipeline for mobile upload via fake event');
          
          // Slot ownership is now tracked by LibraryImageService.beginSelection.
          
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          const fakeEvent = { target: { files: dataTransfer.files } };
          window.handleUpload(fakeEvent, side, uploadIndex);
        } else if (typeof window.processUploadedImage === 'function') {
          // Fallback to processUploadedImage if handleUpload not available
          console.log('📱 Using processUploadedImage fallback');
          window.processUploadedImage(file, side, uploadIndex, dataURL);
        }
      };
      reader.readAsDataURL(file);
      
      // Increment upload count for this side
      mdsState.uploadCounts[side]++;
      
      // Dispatch custom event with index info
      document.dispatchEvent(new CustomEvent('mdsDesignUploaded', {
        detail: { file, side, index: uploadIndex, isVector: false }
      }));
      
      // Also dispatch designUploaded event for compatibility with existing listeners
      document.dispatchEvent(new CustomEvent('designUploaded', {
        detail: { file, side, index: uploadIndex, isVector: false }
      }));
      
      console.log('📱 Design uploaded for side:', side, 'at index:', uploadIndex);
    }
  }
  
  function findUploadInputForSide(side) {
    // Try multiple input ID patterns
    const patterns = [
      `upload-input-${side}-0`,
      `upload-input-${side}`,
      `edit-upload-input-${side}-0`,
      `edit-upload-input-${side}`
    ];
    
    for (const id of patterns) {
      const input = document.getElementById(id);
      if (input) return input;
    }
    
    return null;
  }
  
  // Handle uploads from main page too
  function handleExternalUpload(imageUrl, side) {
    if (imageUrl && side) {
      updateDesignOverlay(imageUrl, side);
    }
  }
  
  // Attach click handlers to design overlays for editing
  function attachDesignOverlayHandlers() {
    // Find all design overlays
    const overlays = document.querySelectorAll('[id^="design-overlay-"], [id^="overlay-wrapper-"], .design-overlay');
    
    overlays.forEach(overlay => {
      // Skip if already has handler
      if (overlay.dataset.mdsHandlerAttached) return;
      overlay.dataset.mdsHandlerAttached = 'true';
      overlay.dataset.clickable = 'true';
      
      // Click handler - first click selects, second click opens appropriate editor
      overlay.addEventListener('click', (e) => {
        if (!mdsState.isMobile) return;
        e.stopPropagation();
        
        const wasAlreadySelected = overlay.classList.contains('selected');
        
        // Check if this is a text layer (has data-text-tool attribute)
        const isTextLayer = overlay.dataset.textTool === 'true' || 
                           overlay.closest('[data-text-tool="true"]') ||
                           overlay.querySelector('[data-text-tool="true"]');
        
        console.log('📱 Overlay click detected:', { wasAlreadySelected, isTextLayer, id: overlay.id });
        
        // Ensure this overlay is the active one for transforms/handles
        if (typeof window.setActiveOverlay === 'function' && overlay.id) {
          window.setActiveOverlay(overlay.id);
        }
        
        // For TEXT layers: always open text editor (even on first click)
        // For IMAGE layers: first click selects, second click opens color editor
        if (isTextLayer) {
          // TEXT LAYER: Always open text editor
          console.log('📱 Opening text editor for text layer');
          
          // Deselect all other overlays first
          document.querySelectorAll('.design-overlay.selected, [id^="design-overlay-"].selected, [id^="overlay-wrapper-"].selected').forEach(el => {
            el.classList.remove('selected');
          });
          overlay.classList.add('selected');
          
          // Get text metadata from UnifiedStateManager or DOM
          // Use lastIndexOf to handle hyphens in side names (e.g., 'front-left')
          const overlayIdStr = overlay.id.replace('design-overlay-', '');
          const lastDashIndex = overlayIdStr.lastIndexOf('-');
          if (lastDashIndex > 0) {
            const side = overlayIdStr.substring(0, lastDashIndex);
            const index = parseInt(overlayIdStr.substring(lastDashIndex + 1), 10);
            
            console.log('📱 Parsed text layer:', { side, index, overlayId: overlay.id });
            
            // Get text metadata for mobile form
            const textMeta = getTextMetadataForOverlay(side, index, overlay);
            
            // Use the text tool handler directly for text layers (populates desktop form)
            if (typeof window.textToolHandleOverlaySelected === 'function') {
              window.textToolHandleOverlaySelected(side, index);
            } else if (typeof window.handleOverlaySelected === 'function') {
              window.handleOverlaySelected(side, index);
            }
            
            // Populate mobile form with text data
            if (textMeta) {
              handleTextClick(textMeta);
              return; // handleTextClick already opens the panel
            }
          }
          
          // Open text panel on mobile (fallback)
          openPanel('text');
        } else if (wasAlreadySelected) {
          // IMAGE LAYER (second click): Open color editor
          console.log('📱 Opening color editor (image overlay was already selected)');
          syncDetectedColors();
          openPanel('logo-colors');
        } else {
          // IMAGE LAYER (first click): Just select the overlay
          console.log('📱 Selecting image overlay (first click)');
          // Deselect all other overlays
          document.querySelectorAll('.design-overlay.selected, [id^="design-overlay-"].selected, [id^="overlay-wrapper-"].selected').forEach(el => {
            el.classList.remove('selected');
          });
          overlay.classList.add('selected');
          
          // Sync detected colors for image layers (but don't open panel)
          syncDetectedColors();
        }
      });
    });
    
    // Find all text overlays
    const textOverlays = document.querySelectorAll('[id^="text-overlay-"], .text-overlay, .text-layer');
    
    textOverlays.forEach(overlay => {
      if (overlay.dataset.mdsHandlerAttached) return;
      overlay.dataset.mdsHandlerAttached = 'true';
      overlay.dataset.clickable = 'true';
      
      // Click handler - first click selects, second click (while selected) opens text editor
      overlay.addEventListener('click', (e) => {
        if (!mdsState.isMobile) return;
        e.stopPropagation();
        
        const wasAlreadySelected = overlay.classList.contains('selected');
        
        console.log('📱 Text overlay click detected, wasAlreadySelected:', wasAlreadySelected);
        
        if (wasAlreadySelected) {
          // Second click on already selected text - open text editor
          console.log('📱 Opening text editor (text was already selected)');
          const textContent = overlay.textContent || overlay.innerText;
          const textData = {
            id: overlay.id,
            element: overlay,
            text: textContent,
            fontFamily: overlay.style.fontFamily || 'Arial',
            fontSize: parseInt(overlay.style.fontSize) || 48,
            color: overlay.style.color || '#000000'
          };
          handleTextClick(textData);
        } else {
          // First click - just select the text overlay
          console.log('📱 Selecting text overlay (first click)');
          // Deselect all other overlays
          document.querySelectorAll('.text-overlay.selected, .text-layer.selected, [id^="text-overlay-"].selected').forEach(el => {
            el.classList.remove('selected');
          });
          overlay.classList.add('selected');
        }
      });
    });
  }
  
  // Click outside overlays to deselect
  function setupClickOutsideToDeselect() {
    document.addEventListener('click', (e) => {
      if (!mdsState.isMobile) return;
      
      // Check if click is on an overlay or inside a panel
      const isOnOverlay = e.target.closest('[id^="design-overlay-"], [id^="overlay-wrapper-"], .design-overlay, [id^="text-overlay-"], .text-overlay, .text-layer');
      const isInsidePanel = e.target.closest('.mds-panel, .mds-nav, #mds-container');
      const isOnHandle = e.target.closest('.handle-delete, .handle-rotate, .handle-resize, .resize-handle, .rotate-handle');
      
      if (!isOnOverlay && !isInsidePanel && !isOnHandle) {
        console.log('📱 Click outside - deselecting all overlays');
        // Deselect all overlays
        document.querySelectorAll('.design-overlay.selected, [id^="design-overlay-"].selected, .text-overlay.selected, .text-layer.selected').forEach(el => {
          el.classList.remove('selected');
        });
      }
    });
  }
  
  // Re-attach handlers when new overlays are added
  function observeNewOverlays() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          // Delay to let DOM settle
          setTimeout(attachDesignOverlayHandlers, 100);
        }
      });
    });
    
    const productImage = document.getElementById('product-image');
    if (productImage) {
      observer.observe(productImage.parentElement || productImage, {
        childList: true,
        subtree: true
      });
    }
  }

  // ============================================
  // TEXT FUNCTIONALITY
  // ============================================
  function handleTextClick(existingTextLayer = null) {
    // If editing existing text, populate the form
    if (existingTextLayer && (existingTextLayer.text || existingTextLayer.id)) {
      console.log('📱 Editing existing text:', existingTextLayer);
      mdsState.editingTextId = existingTextLayer.id || existingTextLayer.element?.id;
      mdsState.editingTextElement = existingTextLayer.element;
      
      // Populate form fields
      if (elements.textInput) {
        elements.textInput.value = existingTextLayer.text || '';
      }
      if (elements.fontSelect) {
        const fontFamily = existingTextLayer.fontFamily || existingTextLayer.font || 'Arial';
        elements.fontSelect.value = fontFamily.replace(/['"]/g, '');
      }
      if (elements.textSize) {
        elements.textSize.value = existingTextLayer.fontSize || existingTextLayer.size || 48;
        updateSizeDisplay();
      }
      // Update button text and panel title
      if (elements.addTextBtn) {
        elements.addTextBtn.textContent = 'Update Text';
      }
      const panelTitle = document.getElementById('mds-text-panel-title');
      if (panelTitle) {
        panelTitle.textContent = 'Edit Text';
      }
      
      // Update color
      const textColor = existingTextLayer.color || '#1a1a2e';
      mdsState.selectedTextColor = textColor;
      if (elements.textColor) {
        elements.textColor.style.backgroundColor = textColor;
      }
      const colorInput = document.getElementById('mds-text-color-input');
      if (colorInput) {
        colorInput.value = textColor.startsWith('#') ? textColor : '#1a1a2e';
      }
      
      // Select the text element visually
      if (existingTextLayer.element) {
        existingTextLayer.element.classList.add('selected');
      }
    } else {
      // New text - clear form
      mdsState.editingTextId = null;
      mdsState.editingTextElement = null;
      if (elements.textInput) elements.textInput.value = '';
      if (elements.fontSelect) elements.fontSelect.value = 'Arial';
      if (elements.textSize) {
        elements.textSize.value = 48;
        updateSizeDisplay();
      }
      if (elements.addTextBtn) {
        elements.addTextBtn.textContent = 'Add Text Layer';
      }
      const panelTitle = document.getElementById('mds-text-panel-title');
      if (panelTitle) {
        panelTitle.textContent = 'Add Text';
      }
      // Reset color to default
      mdsState.selectedTextColor = '#1a1a2e';
      if (elements.textColor) {
        elements.textColor.style.backgroundColor = '#1a1a2e';
      }
      const colorInput = document.getElementById('mds-text-color-input');
      if (colorInput) {
        colorInput.value = '#1a1a2e';
      }
    }
    
    openPanel('text');
  }

  async function handleAddText() {
    const text = elements.textInput?.value?.trim();
    if (!text) {
      alert('Please enter some text');
      return;
    }
    
    const font = elements.fontSelect?.value || '"Inter", "Helvetica Neue", Arial, sans-serif';
    const size = parseInt(elements.textSize?.value || '48', 10);
    const side = mdsState.currentSide;
    const isEditing = !!mdsState.editingTextId;
    
    // Get style options from mobile panel (if available)
    const boldBtn = document.querySelector('.mds-text-btn[data-style="bold"].active');
    const italicBtn = document.querySelector('.mds-text-btn[data-style="italic"].active');
    
    // Get color from mobile color button or state
    let textColor = mdsState.selectedTextColor || '#1a1a2e';
    if (elements.textColor) {
      const bgColor = elements.textColor.style.backgroundColor;
      if (bgColor) {
        // Convert rgb() to hex if needed
        textColor = rgbToHex(bgColor) || textColor;
      }
    }
    
    const options = {
      color: textColor,
      bold: !!boldBtn,
      italic: !!italicBtn,
      align: 'left',
      uppercase: false
    };
    

    
    if (isEditing) {
      // Update existing text layer using the desktop update function
      // Parse side and index from the editing ID (format: design-overlay-{side}-{index})
      const overlayId = mdsState.editingTextId;
      let editSide = side;
      let editIndex = 0;
      
      if (overlayId && overlayId.startsWith('design-overlay-')) {
        const parts = overlayId.replace('design-overlay-', '');
        const lastDash = parts.lastIndexOf('-');
        if (lastDash > 0) {
          editSide = parts.substring(0, lastDash);
          editIndex = parseInt(parts.substring(lastDash + 1), 10);
        }
      }
      

      
      if (typeof window.updateTextLayerOnDesign === 'function') {
        const success = await window.updateTextLayerOnDesign(editSide, editIndex, text, font, size, options);
        if (success) {

          // Update local tracking
          const existingIdx = mdsState.textLayers.findIndex(t => t.id === mdsState.editingTextId);
          if (existingIdx >= 0) {
            mdsState.textLayers[existingIdx] = { id: mdsState.editingTextId, text, font, size, side: editSide, ...options };
          }
        }
      } else {
        console.warn('📱 updateTextLayerOnDesign not available');
      }
    } else {
      // Add new text layer using the main text tool manager
      if (typeof window.addTextToDesign === 'function') {
        const success = await window.addTextToDesign(text, font, size, side, options);
        if (success) {
          // Track locally
          mdsState.textLayers.push({ id: `text-${Date.now()}`, text, font, size, side, ...options });
        }
      } else {
        // Fallback: Create mobile-only text overlay (less functional)
        console.warn('📱 addTextToDesign not available, using fallback');
        const textLayer = { id: `text-${Date.now()}`, text, font, size, side, ...options };
        mdsState.textLayers.push(textLayer);
        addTextToOverlay(textLayer);
      }
    }
    
    // Reset form and state
    if (elements.textInput) elements.textInput.value = '';
    mdsState.editingTextId = null;
    mdsState.editingTextElement = null;
    if (elements.addTextBtn) elements.addTextBtn.textContent = 'Add Text Layer';
    
    closeAllPanels();
    showDesignBadge();
    
    // Update floating actions visibility
    setTimeout(updateFloatingActionsVisibility, 100);
  }
  
  function addTextToOverlay(textLayer) {
    if (!elements.designOverlay) return;
    
    // Check if text element already exists
    let textEl = document.getElementById(textLayer.id);
    
    if (!textEl) {
      textEl = document.createElement('div');
      textEl.id = textLayer.id;
      textEl.className = 'mds-text-layer';
      textEl.style.cssText = `
        position: absolute;
        cursor: pointer;
        user-select: none;
        text-align: center;
        max-width: 80%;
        word-wrap: break-word;
        pointer-events: auto;
      `;
      
      // Add click to edit
      textEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const layer = mdsState.textLayers.find(t => t.id === textLayer.id);
        if (layer) handleTextClick(layer);
      });
      
      elements.designOverlay.appendChild(textEl);
    }
    
    // Update text element
    textEl.textContent = textLayer.text;
    textEl.style.fontFamily = textLayer.font;
    textEl.style.fontSize = `${textLayer.size}px`;
    textEl.style.color = textLayer.color || '#000000';
  }
  
  function showTextLayersForCurrentSide() {
    const sideLayers = mdsState.textLayers.filter(t => t.side === mdsState.currentSide);
    
    // Hide all text layers first
    mdsState.textLayers.forEach(layer => {
      const el = document.getElementById(layer.id);
      if (el) el.style.display = 'none';
    });
    
    // Show current side's layers
    sideLayers.forEach(layer => {
      const el = document.getElementById(layer.id);
      if (el) {
        el.style.display = 'block';
      } else {
        addTextToOverlay(layer);
      }
    });
  }

  function updateSizeDisplay() {
    if (elements.sizeValue && elements.textSize) {
      elements.sizeValue.textContent = `${elements.textSize.value}px`;
    }
  }

  // ============================================
  // COLOR FUNCTIONALITY
  // ============================================
  function syncProductColors() {
    if (!elements.productColors || !elements.mainColorSelectors) return;
    
    // Clone color selectors from main page
    const colors = elements.mainColorSelectors.querySelectorAll('.color-selector');
    elements.productColors.innerHTML = '';
    
    colors.forEach(colorEl => {
      const clone = colorEl.cloneNode(true);
      clone.classList.add('mds-color-chip');
      
      // Re-attach click handler
      clone.addEventListener('click', () => {
        // Trigger original click
        colorEl.click();
        // Update UI
        syncProductColors();
        closeAllPanels();
      });
      
      elements.productColors.appendChild(clone);
    });
  }

  function syncDetectedColors() {
    if (!elements.detectedColors) return;
    

    
    // Try multiple sources for detected colors
    const sources = [
      '#edit-colors-sidebar',
      '#detected-colors-container',
      '.detected-colors-list',
      '[data-detected-colors]',
      '#color-chips-container'
    ];
    
    for (const selector of sources) {
      const mainDetected = document.querySelector(selector);
      if (mainDetected && mainDetected.children.length > 0) {

        // Clone the content
        elements.detectedColors.innerHTML = mainDetected.innerHTML;
        
        // Prefer the new detected-color-card layout used in the desktop sidebar
        const cards = elements.detectedColors.querySelectorAll('.detected-color-card');
        if (cards.length > 0) {
          cards.forEach(card => {
            const slotIndex = card.dataset.slotIndex;

            // Map desktop card + revert button for this slot
            const originalCard = slotIndex !== undefined
              ? mainDetected.querySelector(`.detected-color-card[data-slot-index="${slotIndex}"]`)
              : null;
            const originalRevertBtn = slotIndex !== undefined
              ? mainDetected.querySelector(`.detected-color-card[data-slot-index="${slotIndex}"] .slot-revert-btn`)
              : null;

            // Card click -> select slot + open Pantone library
            card.addEventListener('click', (e) => {
              e.stopPropagation();
              let handled = false;

              // 1) Forward click to the original desktop card if possible
              if (originalCard) {
                originalCard.click();
                handled = true;
              }

              // 2) Fallback: call global handler directly if exported
              if (!handled && typeof window.handleDetectedColorSlotClick === 'function' && slotIndex !== undefined) {
                const idx = parseInt(slotIndex, 10);
                const stateModule = window.ProductConfigurator?.State || window.State || {};
                const colors = stateModule.editColors || [];
                const name = Array.isArray(colors) ? colors[idx] : null;
                window.handleDetectedColorSlotClick(idx, name);
                handled = true;
              }

              // Ensure the mobile Logo Colors panel stays open
              openPanel('logo-colors');
            });

            // Slot-level revert button inside the mobile clone
            const mobileRevertBtn = card.querySelector('.slot-revert-btn');
            if (mobileRevertBtn) {
              mobileRevertBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                // Prefer calling the original desktop revert button logic
                if (originalRevertBtn) {
                  originalRevertBtn.click();
                } else if (typeof window.revertColorSlot === 'function' && slotIndex !== undefined) {
                  // Fallback: call global revertColorSlot directly
                  const overlayId = window.activeOverlayId;
                  const activeOverlay = overlayId ? document.getElementById(overlayId) : null;
                  const side = activeOverlay?.dataset?.side || window.ProductConfigurator?.State?.currentSide;
                  const idx = activeOverlay?.dataset?.index
                    ? parseInt(activeOverlay.dataset.index, 10)
                    : (typeof window.ProductConfigurator?.State?.getCurrentImageIndex === 'function'
                        ? window.ProductConfigurator.State.getCurrentImageIndex(side)
                        : 0);
                  if (side) {
                    window.revertColorSlot(side, idx, parseInt(slotIndex, 10));
                  }
                }
              });
            }
          });
        } else {
          // Fallback: generic chips or legacy markup
          elements.detectedColors.querySelectorAll('.color-chip, .detected-color, [data-color]').forEach(chip => {
            chip.addEventListener('click', (e) => {
              e.stopPropagation();
              const originalId = chip.id || chip.dataset.colorIndex;
              const originalChip = mainDetected.querySelector(`#${originalId}, [data-color-index="${originalId}"]`);
              if (originalChip) {
                originalChip.click();
              }
              openPanel('logo-colors');
            });
          });
        }
        return;
      }
    }
    
    // Check window.detectedColors global
    if (window.detectedColors && window.detectedColors.length > 0) {

      elements.detectedColors.innerHTML = '';
      
      window.detectedColors.forEach((color, idx) => {
        const chip = document.createElement('button');
        chip.className = 'detected-color-chip';
        chip.style.cssText = `
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e2e8f0;
          cursor: pointer;
          background-color: ${color.hex || color};
          margin: 4px;
        `;
        chip.dataset.colorIndex = idx;
        chip.title = color.name || color.hex || color;
        chip.addEventListener('click', () => {
          // Use global handler if available so we select the slot and open Pantone library
          const nameOrHex = color.name || color.hex || color;
          if (typeof window.handleDetectedColorChipClick === 'function') {
            window.handleDetectedColorChipClick(nameOrHex);
          }
          openPanel('logo-colors');
        });
        elements.detectedColors.appendChild(chip);
      });
      return;
    }
    

  }

  /**
   * Sync mobile background controls from the desktop version.
   * Creates the same "Remove Background" / "Show BG" buttons for mobile.
   */
  function syncMobileBackgroundControls() {
    const container = elements.mobileBackgroundControls;
    const list = elements.mobileBackgroundList;
    
    if (!container || !list) return;
    

    
    // Get State from ProductConfigurator or window
    const State = window.ProductConfigurator?.State || window.State;
    if (!State) {
      container.classList.add('hidden');
      return;
    }
    
    // Collect currently selected image info
    const images = [];
    let selectedSide = null;
    let selectedIndex = 0;
    
    // Try to get from activeOverlayId
    if (typeof window.activeOverlayId === 'string' && window.activeOverlayId) {
      const parts = window.activeOverlayId.split('-');
      selectedSide = parts[2];
      selectedIndex = parseInt(parts[3], 10) || 0;
    } else if (State.currentSide) {
      selectedSide = State.currentSide;
      selectedIndex = typeof State.getCurrentImageIndex === 'function'
        ? State.getCurrentImageIndex(selectedSide)
        : 0;
    }
    
    if (selectedSide) {
      const sideImages = State.uploadedImages?.[selectedSide];
      const persisted = Array.isArray(sideImages) ? sideImages[selectedIndex] : sideImages;
      const raw = Array.isArray(State.rawUploadedImages?.[selectedSide])
        ? State.rawUploadedImages[selectedSide][selectedIndex]
        : State.rawUploadedImages?.[selectedSide];
      const original = Array.isArray(State.originalImages?.[selectedSide])
        ? State.originalImages[selectedSide][selectedIndex]
        : State.originalImages?.[selectedSide];
      const displayUrl = raw || original || persisted || null;
      if (displayUrl) {
        images.push({ side: selectedSide, index: selectedIndex, url: displayUrl });
      }
    }
    
    // Hide container if no images
    if (images.length === 0) {
      container.classList.add('hidden');
      return;
    }
    
    container.classList.remove('hidden');
    list.innerHTML = '';
    
    // Create control for each image
    images.forEach(({ side, index, url }) => {
      const bgRemoved = typeof window.isBackgroundRemoved === 'function' 
        ? window.isBackgroundRemoved(side, index) 
        : false;
      const hasOriginal = typeof window.getOriginalImage === 'function' 
        ? !!window.getOriginalImage(side, index) 
        : false;
      const hasBgRemoved = typeof window.getBgRemovedImage === 'function' 
        ? !!window.getBgRemovedImage(side, index) 
        : false;
      const sideName = side.charAt(0).toUpperCase() + side.slice(1);
      
      // Check if image is already transparent
      const alreadyTransparent = typeof window.isAlreadyTransparent === 'function' 
        ? window.isAlreadyTransparent(side, index) 
        : false;
      
      // Check if image has too many colors (>5)
      const detectedColors = typeof State.getDetectedColorsForImage === 'function'
        ? (State.getDetectedColorsForImage(side, index) || [])
        : [];
      const hasTooManyColors = detectedColors.length > 5;
      
      // Check if image is full-color
      const complexityData = typeof State.getImageComplexityData === 'function' 
        ? State.getImageComplexityData(side, index) 
        : null;
      const isFullColor = complexityData?.isFullColor === true;
      
      // Determine status text
      let statusText = 'Original';
      if (isFullColor) {
        statusText = 'Full-color image';
      } else if (alreadyTransparent) {
        statusText = 'Already transparent';
      } else if (bgRemoved) {
        statusText = 'Background removed';
      }
      
      const showBgControls = !isFullColor;
      
      const item = document.createElement('div');
      item.className = 'flex items-center gap-3 p-3 bg-gray-50 rounded-xl';
      
      item.innerHTML = `
        <img src="${url}" class="w-12 h-12 object-contain rounded-lg border border-gray-200 bg-white" alt="${sideName}">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-700 truncate">${sideName}</p>
          <p class="text-xs ${isFullColor ? 'text-purple-600' : alreadyTransparent ? 'text-green-600' : 'text-gray-500'}">${statusText}</p>
        </div>
        ${showBgControls ? `
        <div class="flex gap-2">
          ${bgRemoved && hasOriginal && !alreadyTransparent && !hasTooManyColors ? `
            <button type="button" class="mds-revert-bg-btn flex items-center gap-1 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors" data-side="${side}" data-index="${index}" title="Show background">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Show Background
            </button>
          ` : ''}
          ${alreadyTransparent ? `
            <button type="button" class="flex items-center gap-1 px-3 py-2 text-[10px] font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed" disabled title="Image already has transparency">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              No Background
            </button>
          ` : !bgRemoved && !hasTooManyColors ? `
            <button type="button" class="mds-remove-bg-btn flex items-center gap-1 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors" data-side="${side}" data-index="${index}" title="Remove background">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0 a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
              Remove Background
            </button>
          ` : ''}
        </div>
        ` : ''}
      `;
      
      list.appendChild(item);
    });
    
    // Attach event listeners for Show Background buttons
    list.querySelectorAll('.mds-revert-bg-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const side = btn.dataset.side;
        const index = parseInt(btn.dataset.index, 10);
        
        console.log(`📱 Show Background: ${side}[${index}]`);
        btn.disabled = true;
        btn.innerHTML = '<span class="animate-pulse text-xs">...</span>';
        
        if (typeof window.toggleImageBackground === 'function') {
          await window.toggleImageBackground(side, index, true);
        }
        
        // Refresh the controls after toggle
        setTimeout(() => syncMobileBackgroundControls(), 100);
      });
    });
    
    // Attach event listeners for Remove Background buttons
    list.querySelectorAll('.mds-remove-bg-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const side = btn.dataset.side;
        const index = parseInt(btn.dataset.index, 10);
        
        // Block for full-color images
        const complexityData = typeof State.getImageComplexityData === 'function' 
          ? State.getImageComplexityData(side, index) 
          : null;
        if (complexityData?.isFullColor === true) {
          console.log(`📱 Background removal blocked for full-color image: ${side}[${index}]`);
          return;
        }
        
        console.log(`📱 Remove Background: ${side}[${index}]`);
        btn.disabled = true;
        btn.innerHTML = '<span class="animate-pulse text-xs">...</span>';
        
        // Check if we need to compute the BG-removed variant first
        let hasBgRemoved = typeof window.getBgRemovedImage === 'function' 
          ? window.getBgRemovedImage(side, index) 
          : null;
        
        if (!hasBgRemoved && typeof window.computeBgRemovedVariant === 'function') {
          hasBgRemoved = await window.computeBgRemovedVariant(side, index);
        }
        
        if (hasBgRemoved && typeof window.toggleImageBackground === 'function') {
          await window.toggleImageBackground(side, index, false);
        }
        
        // Refresh the controls after toggle
        setTimeout(() => syncMobileBackgroundControls(), 100);
      });
    });
    

  }

  // Mount the desktop Pantone library into the mobile Logo Colors panel
  // so mobile users can see and use the same replacement UI.
  function mountPantoneLibraryForMobile() {
    const pantoneLibrary = document.getElementById('pantone-library');
    if (!pantoneLibrary || !elements.colorPalette) return;

    const isMobileViewport = window.innerWidth <= MDS_CONFIG.breakpoint;
    if (!isMobileViewport) return;

    // If it's already inside the mobile palette, nothing to do
    if (pantoneLibrary.parentElement === elements.colorPalette) {
      pantoneLibrary.classList.remove('hidden');
      return;
    }

    // Clear any placeholder content and move the real library in
    elements.colorPalette.innerHTML = '';
    elements.colorPalette.appendChild(pantoneLibrary);
    pantoneLibrary.classList.remove('hidden');
  }

  // ============================================
  // DESIGN STATE
  // ============================================
  function showDesignBadge() {
    mdsState.hasDesign = true;

    if (elements.designBadge) {
      elements.designBadge.classList.remove('hidden');
      // Auto-hide the "Design Added" confirmation after a few seconds. The
      // hasDesign flag stays true; this only hides the visual badge so it
      // doesn't sit on the mockup forever.
      if (mdsState._designBadgeHideTimer) {
        clearTimeout(mdsState._designBadgeHideTimer);
      }
      mdsState._designBadgeHideTimer = setTimeout(() => {
        elements.designBadge?.classList.add('hidden');
        mdsState._designBadgeHideTimer = null;
      }, 2500);
    }

    if (elements.floatingActions) {
      elements.floatingActions.classList.add('has-design');
    }

    if (elements.tapIndicator) {
      elements.tapIndicator.classList.remove('hidden');
      // Auto-hide after 3 seconds
      setTimeout(() => {
        elements.tapIndicator?.classList.add('hidden');
      }, 3000);
    }
  }

  function hideDesignBadge() {
    mdsState.hasDesign = false;

    if (mdsState._designBadgeHideTimer) {
      clearTimeout(mdsState._designBadgeHideTimer);
      mdsState._designBadgeHideTimer = null;
    }

    if (elements.designBadge) {
      elements.designBadge.classList.add('hidden');
    }
    
    if (elements.floatingActions) {
      elements.floatingActions.classList.remove('has-design');
    }
    
    if (elements.tapIndicator) {
      elements.tapIndicator.classList.add('hidden');
    }
  }

  // ============================================
  // NAVIGATION
  // ============================================
  function handleNavNext() {
    // Scroll to size/quantity section
    const sizeGrid = document.getElementById('size-grid');
    const qtySection = sizeGrid?.closest('.step-section');
    
    if (qtySection) {
      qtySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================
  function attachEventListeners() {
    if (mdsState.listenersBound) return;
    mdsState.listenersBound = true;

    // Resize handler
    window.addEventListener('resize', debounce(updateMobileVisibility, MDS_CONFIG.debounceDelay));
    
    // Floating buttons — direct listeners
    elements.uploadBtn?.addEventListener('click', handleUploadClick);
    elements.textBtn?.addEventListener('click', handleTextClick);

    // Delegated safety net: also handle taps via document so the buttons
    // still work if the direct listeners couldn't bind (element not in DOM
    // at cache time, replaced by another script, or rebuilt later). A
    // micro-stamp dedupes against the direct listener firing the same event.
    document.addEventListener('click', (e) => {
      if (e.__mdsHandled) return;
      const uploadHit = e.target && e.target.closest && e.target.closest('#mds-upload-btn');
      if (uploadHit) {
        e.__mdsHandled = true;
        handleUploadClick();
        return;
      }
      const textHit = e.target && e.target.closest && e.target.closest('#mds-text-btn');
      if (textHit) {
        e.__mdsHandled = true;
        handleTextClick();
      }
    });
    // Mark direct-handler invocations so the delegated listener skips them.
    if (elements.uploadBtn) {
      elements.uploadBtn.addEventListener('click', (e) => { e.__mdsHandled = true; }, true);
    }
    if (elements.textBtn) {
      elements.textBtn.addEventListener('click', (e) => { e.__mdsHandled = true; }, true);
    }
    
    // Bottom navigation
    elements.navItems?.forEach(item => {
      item.addEventListener('click', () => {
        const panel = item.dataset.panel;
        if (panel) openPanel(panel);
      });
    });
    
    elements.navNext?.addEventListener('click', handleNavNext);
    
    // Panel close buttons
    document.querySelectorAll('[data-close-panel]').forEach(btn => {
      btn.addEventListener('click', closeAllPanels);
    });
    
    // Backdrop click
    elements.panelBackdrop?.addEventListener('click', closeAllPanels);
    
    // Upload options
    elements.uploadFile?.addEventListener('click', handleUploadFile);
    elements.uploadCamera?.addEventListener('click', handleUploadCamera);
    elements.uploadLibrary?.addEventListener('click', handleUploadLibrary);
    
    // Text controls
    elements.addTextBtn?.addEventListener('click', handleAddText);
    elements.textSize?.addEventListener('input', updateSizeDisplay);
    
    // Text style buttons
    document.querySelectorAll('.mds-text-btn[data-style]').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.classList.toggle('active');
      });
    });
    
    // Text color button - opens Pantone color picker panel
    if (elements.textColor) {
      const colorPickerPanel = document.getElementById('mds-text-color-picker');
      const pantoneGrid = document.getElementById('mds-text-pantone-grid');
      const colorSearch = document.getElementById('mds-text-color-search');
      
      // Populate Pantone grid on first open
      let pantonePopulated = false;
      
      elements.textColor.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (colorPickerPanel) {
          const isHidden = colorPickerPanel.classList.contains('hidden');
          colorPickerPanel.classList.toggle('hidden');
          
          // Populate Pantone grid on first open
          if (isHidden && !pantonePopulated && pantoneGrid) {
            populateMobilePantoneGrid(pantoneGrid);
            pantonePopulated = true;
          }
        }
      });
      
      // Search functionality - use improved filter with numeric code matching
      colorSearch?.addEventListener('input', (e) => {
        filterMobilePantoneSwatches(pantoneGrid, e.target.value);
      });
      
      // Close picker when clicking outside
      document.addEventListener('click', (e) => {
        if (colorPickerPanel && !colorPickerPanel.classList.contains('hidden')) {
          if (!colorPickerPanel.contains(e.target) && e.target !== elements.textColor) {
            colorPickerPanel.classList.add('hidden');
          }
        }
      });
    }
    
    // Product image click - detect what was clicked (image or text)
    elements.productWrapper?.addEventListener('click', (e) => {
      if (!mdsState.isMobile) return;
      if (e.target.closest('.mds-floating-btn, .mds-side-thumb, .mds-quick-btn, .resize-handle, .rotate-handle')) return;
      
      // Check if clicked on a text overlay
      const textOverlay = e.target.closest('[id^="text-overlay-"], .text-overlay, .text-layer');
      if (textOverlay) {
        console.log('📱 Text overlay clicked - opening text editor');
        const textContent = textOverlay.textContent || textOverlay.innerText;
        const textData = {
          id: textOverlay.id,
          text: textContent,
          fontFamily: textOverlay.style.fontFamily || 'Arial',
          fontSize: parseInt(textOverlay.style.fontSize) || 48,
          color: textOverlay.style.color || '#000000'
        };
        handleTextClick(textData);
        return;
      }
      
      // Check if clicked on a design/image overlay
      const designOverlay = e.target.closest('[id^="design-overlay-"], [id^="overlay-wrapper-"], .design-overlay');
      if (designOverlay) {
        // Mark this overlay as active so transform handles stay visible
        if (typeof window.setActiveOverlay === 'function' && designOverlay.id) {
          window.setActiveOverlay(designOverlay.id);
        }
        // Select the overlay visually
        designOverlay.classList.add('selected');
        // Sync detected colors and background controls (no auto-open)
        syncDetectedColors();
        syncMobileBackgroundControls();
        return;
      }
      
      // General click on product area (not on design/text) -> deselect active overlay
      if (mdsState.hasDesign && typeof window.deselectAllOverlays === 'function') {
        window.deselectAllOverlays();
      }
    });
    
    // Attach click handlers to existing design overlays
    attachDesignOverlayHandlers();
    
    // Observe for new overlays being added
    observeNewOverlays();
    
    // Listen for design uploads from main page
    const handleDesignUploaded = (e) => {
      console.log('📱 Design uploaded event received');
      showDesignBadge();
      // Set flag to auto-open colors panel when colors are detected
      mdsState.pendingAutoOpenColors = true;
      // Delay to allow color detection to complete
      setTimeout(() => { syncDetectedColors(); syncMobileBackgroundControls(); }, 500);
      setTimeout(() => { syncDetectedColors(); syncMobileBackgroundControls(); }, 1500);
    };
    
    // Listen for both event names to ensure compatibility
    document.addEventListener('designUploaded', handleDesignUploaded);
    document.addEventListener('mdsDesignUploaded', handleDesignUploaded);
    
    document.addEventListener('designRemoved', (e) => {
      console.log('📱 Design removed event received');
      // Update floating actions visibility after design removal
      setTimeout(updateFloatingActionsVisibility, 100);
      hideDesignBadge();
    });
    
    // Listen for design restoration (when user clicks restore after removing)
    document.addEventListener('designRestored', (e) => {
      console.log('📱 Design restored event received');
      setTimeout(updateFloatingActionsVisibility, 100);
      showDesignBadge();
    });
    
    // Listen for overlay changes (add/remove)
    document.addEventListener('overlayUpdated', (e) => {
      console.log('📱 Overlay updated event received');
      setTimeout(updateFloatingActionsVisibility, 100);
    });
    
    const refreshSideNavForColor = (requestedColor) => {
      if (mdsState.pendingColorRefreshTimer) {
        clearTimeout(mdsState.pendingColorRefreshTimer);
      }
      mdsState.pendingColorRefreshTimer = setTimeout(() => {
        mdsState.pendingColorRefreshTimer = null;
        const newColor = requestedColor || window.selectedColor || '';

        const now = Date.now();
        const lastColor = mdsState.__lastColorRefreshColor || '';
        const lastSide = mdsState.__lastColorRefreshSide || '';
        const lastAt = mdsState.__lastColorRefreshAt || 0;
        const sideKey = String(mdsState.currentSide || 'front').toLowerCase();
        if (newColor && lastColor === newColor && lastSide === sideKey && (now - lastAt) < 900) {
          return;
        }
        mdsState.__lastColorRefreshColor = newColor;
        mdsState.__lastColorRefreshSide = sideKey;
        mdsState.__lastColorRefreshAt = now;

        initializeSideNav();
        const availableSides = detectAvailableSides();
        let targetSide = mdsState.currentSide || 'front';

        // If the current side does not exist for this color, fall back to the first available
        if (!availableSides.includes(targetSide)) {
          targetSide = availableSides[0] || 'front';
        }

        let newThumbImg = getThumbImageForSide(targetSide, newColor);

        // If no image for target side, try front as a safe fallback
        if ((!newThumbImg || newThumbImg === elements.productImage?.src) && targetSide !== 'front' && availableSides.includes('front')) {
          const fallbackImg = getThumbImageForSide('front', newColor);
          if (fallbackImg) {
            targetSide = 'front';
            newThumbImg = fallbackImg;
          }
        }

        // Persist the (possibly adjusted) side
        mdsState.currentSide = targetSide;

        // Update side nav and tabs active state
        const thumbs = elements.sideNav?.querySelectorAll('.mds-side-thumb');
        thumbs?.forEach(thumb => {
          thumb.classList.toggle('active', thumb.dataset.side === targetSide);
        });
        updateSideTabsActive(targetSide);

        // Update main product image
        if (elements.productImage && newThumbImg) {
          const currentSrc = String(elements.productImage.getAttribute('src') || '').split('?')[0];
          const nextSrc = String(newThumbImg || '').split('?')[0];
          if (nextSrc && currentSrc !== nextSrc) {
            console.log('📱 Updating product image to:', newThumbImg);
            window.__deProgrammaticProductImageUpdateUntil = Date.now() + 350;
            elements.productImage.src = newThumbImg;
          }
        }

        // Keep main page/overlay side in sync
        if (typeof window.switchSideContext === 'function') {
          window.switchSideContext(targetSide);
        } else if (typeof window.updatePrintSideOverlay === 'function') {
          window.updatePrintSideOverlay(targetSide);
        }
      }, 180);
    };
    mdsState.refreshSideNavForColor = refreshSideNavForColor;

    // Listen for color changes - update thumbnails and main image
    // Note: colorSelected is dispatched on window, not document
    window.addEventListener('colorSelected', (e) => {
      const phase = String(e.detail?.phase || '').toLowerCase();
      const color = e.detail?.color || window.selectedColor || '';
      const source = String(e.detail?.source || 'unknown');
      if (!mdsState.initialSyncCompleted && phase !== 'user' && phase !== 'restoration') {
        mdsState.pendingStartupColor = color || '';
        return;
      }
      const now = Date.now();
      const eventKey = `${String(color).toLowerCase()}::${phase || 'runtime'}::${String(mdsState.currentSide || 'front').toLowerCase()}`;
      mdsState.__recentColorEvents = mdsState.__recentColorEvents || new Map();
      const last = mdsState.__recentColorEvents.get(eventKey) || 0;
      if ((now - last) < 900) {
        return;
      }
      mdsState.__recentColorEvents.set(eventKey, now);

      console.log('📱 colorSelected event received:', color, 'phase:', phase || 'runtime', 'source:', source);
      syncProductColors();
      refreshSideNavForColor(color);
    });

    window.addEventListener('configurator:gallery-color-updated', (e) => {
      const color = e.detail?.color || window.selectedColor || '';
      refreshSideNavForColor(color);
    });
    
    // Listen for color detection complete (initial palette build)
    document.addEventListener('colorsDetected', () => {
      console.log('📱 Colors detected event received');
      syncDetectedColors();
      syncMobileBackgroundControls();
      
      // Auto-open logo colors panel after fresh upload on mobile
      if (mdsState.isMobile && mdsState.pendingAutoOpenColors) {
        mdsState.pendingAutoOpenColors = false;
        console.log('📱 Auto-opening logo colors panel after color detection');
        openPanel('logo-colors');
      }
    });

    // Listen for subsequent color changes/reverts triggered by Pantone library
    // so the mobile detected-color chips update immediately.
    document.addEventListener('colorsUpdated', () => {
      console.log('📱 colorsUpdated event received');
      syncDetectedColors();
      syncMobileBackgroundControls();
    });

    // Sync mobile Logo Colors search with the desktop Pantone search input
    // so typing in the mobile field actually filters the shared Pantone grid
    // and pressing Enter applies the same behavior (auto-apply first match
    // or custom hex) as desktop.
    const mobileSearch = elements.colorSearch;
    const desktopSearch = document.getElementById('color-search-input');
    if (mobileSearch && desktopSearch) {
      const syncFromMobile = debounce(() => {
        desktopSearch.value = mobileSearch.value || '';
        desktopSearch.dispatchEvent(new Event('input', { bubbles: true }));
      }, MDS_CONFIG.debounceDelay);

      mobileSearch.addEventListener('input', syncFromMobile);

      mobileSearch.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        desktopSearch.value = mobileSearch.value || '';
        // Trigger the same Enter handler that desktop uses
        desktopSearch.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'Enter',
          bubbles: true
        }));
      });
    }
    
    // Listen for image processing complete
    document.addEventListener('imageProcessed', (e) => {
      const { side, imageUrl } = e.detail || {};
      if (side && imageUrl) {
        updateDesignOverlay(imageUrl, side);
        // Update floating actions after image is processed
        setTimeout(updateFloatingActionsVisibility, 100);
        
        // HYBRID CACHE: Update cache after new image upload
        if (window.designCacheManager && window.unifiedStateManager) {
          try {
            const state = window.unifiedStateManager.getState();
            const productId = state.metadata?.productId || window.productConfig?.product_id;
            const variantId = state.metadata?.variantId || window.currentVariantId;
            window.designCacheManager.saveToCache(state, {
              productId: productId,
              variantId: variantId
            });
            console.log('📦 DesignCache: Updated cache after image upload');
          } catch (err) {
            console.warn('📦 DesignCache: Failed to update cache after upload:', err.message);
          }
        }
      }
      syncDetectedColors();
      syncMobileBackgroundControls();
    });
    
    // Listen for cart/reorder restoration complete
    document.addEventListener('configuratorStateRestored', (e) => {
      console.log('📱 Configurator state restored event received');
      // First sync MDS internal state with restored designs
      const unifiedImages = window.unifiedStateManager?.getState?.()?.imagesByArea || window.State?.uploadedImages || {};
      Object.keys(unifiedImages).forEach(side => {
        const sideImages = unifiedImages[side];
        if (Array.isArray(sideImages)) {
          mdsState.uploadedDesigns[side] = sideImages.map(img => img?.url || null);
          console.log(`📱 Synced MDS state for ${side}: ${mdsState.uploadedDesigns[side].filter(Boolean).length} images`);
        }
      });
      // Update floating actions visibility after restoration with multiple delays
      updateFloatingActionsVisibility(); // Immediate
      setTimeout(updateFloatingActionsVisibility, 300);
      setTimeout(updateFloatingActionsVisibility, 800);
      setTimeout(updateFloatingActionsVisibility, 1500);
    });
    
    // Listen for overlay creation (covers restoration and new uploads)
    document.addEventListener('overlayCreated', (e) => {
      console.log('📱 Overlay created event received');
      setTimeout(updateFloatingActionsVisibility, 100);
    });
    
    // Listen for design cache loaded event (immediate UI feedback before full restoration)
    window.addEventListener('designCacheLoaded', (e) => {
      console.log('📱 Design cache loaded event received', e.detail);
      const { hasDesigns, sidesWithDesigns, color } = e.detail || {};
      
      if (hasDesigns) {
        // Immediately sync MDS state with cached data
        if (sidesWithDesigns && Array.isArray(sidesWithDesigns)) {
          sidesWithDesigns.forEach(side => {
            if (!mdsState.uploadedDesigns[side]) {
              mdsState.uploadedDesigns[side] = [];
            }
            // Mark as having design (placeholder until full restoration)
            mdsState.uploadedDesigns[side][0] = 'cached';
          });
          console.log(`📱 Synced MDS state from cache: ${sidesWithDesigns.join(', ')} have designs`);
        }
        
        // Update floating actions immediately to hide add image/text buttons
        updateFloatingActionsVisibility();
      }
    });

    // Listen for restoration-complete event (fires after all restoration is done)
    // Note: This event is dispatched on window, not document
    window.addEventListener('restoration-complete', (e) => {
      // Update floating actions with multiple delays to ensure DOM is ready
      setTimeout(updateFloatingActionsVisibility, 100);
      setTimeout(updateFloatingActionsVisibility, 500);
      setTimeout(updateFloatingActionsVisibility, 1000);
    });
    
    // Rebuild the side nav once print_areas land, so non-print sides drop out.
    window.addEventListener('productConfigReady', () => {
      mdsState._sideNavSignature = '';
      try { initializeSideNav(); } catch (_) {}
    });

    // Swipe to close panels (touch events)
    let touchStartY = 0;
    document.querySelectorAll('.mds-panel').forEach(panel => {
      panel.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      
      panel.addEventListener('touchmove', (e) => {
        const touchY = e.touches[0].clientY;
        const diff = touchY - touchStartY;
        
        // If swiping down significantly, close panel
        if (diff > 100) {
          closeAllPanels();
        }
      }, { passive: true });
    });
  }

  /**
   * Sync mobile font dropdown with desktop TEXT_TOOL_FONTS
   */
  function syncMobileFontDropdown() {
    const mobileFontSelect = document.getElementById('mds-font-select');
    const desktopFontSelect = document.getElementById('text-tool-font');
    
    if (!mobileFontSelect) return;
    
    // If desktop font select has options, copy them
    if (desktopFontSelect && desktopFontSelect.options.length > 0) {
      mobileFontSelect.innerHTML = '';
      Array.from(desktopFontSelect.options).forEach(opt => {
        const newOpt = document.createElement('option');
        newOpt.value = opt.value;
        newOpt.textContent = opt.textContent;
        newOpt.style.fontFamily = opt.value;
        mobileFontSelect.appendChild(newOpt);
      });
      console.log(`📱 Synced ${mobileFontSelect.options.length} fonts from desktop`);
      return;
    }
    
    // Fallback: define fonts directly (same as TEXT_TOOL_FONTS in text-tool-manager)
    const TEXT_TOOL_FONTS = [
      { label: 'Inter', value: '"Inter", "Helvetica Neue", Arial, sans-serif' },
      { label: 'Montserrat', value: '"Montserrat", "Helvetica Neue", Arial, sans-serif' },
      { label: 'Nunito', value: '"Nunito", "Helvetica Neue", Arial, sans-serif' },
      { label: 'Playfair Display', value: '"Playfair Display", Georgia, serif' },
      { label: 'Bebas Neue', value: '"Bebas Neue", "Trebuchet MS", sans-serif' },
      { label: 'Courier Prime', value: '"Courier Prime", "Courier New", monospace' },
      { label: 'Dancing Script', value: '"Dancing Script", "Comic Sans MS", cursive' },
      { label: 'Pacifico', value: '"Pacifico", "Comic Sans MS", cursive' },
      { label: 'Allura', value: '"Allura", "Brush Script MT", cursive' },
      { label: 'Great Vibes', value: '"Great Vibes", "Brush Script MT", cursive' },
      { label: 'Satisfy', value: '"Satisfy", "Brush Script MT", cursive' }
    ];
    
    mobileFontSelect.innerHTML = '';
    TEXT_TOOL_FONTS.forEach(font => {
      const opt = document.createElement('option');
      opt.value = font.value;
      opt.textContent = font.label;
      opt.style.fontFamily = font.value;
      mobileFontSelect.appendChild(opt);
    });
    
    console.log(`📱 Populated mobile font dropdown with ${TEXT_TOOL_FONTS.length} fonts`);
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    console.log('📱 Initializing Mobile Design Studio...');
    
    cacheElements();
    attachEventListeners();
    updateMobileVisibility();
    setupClickOutsideToDeselect();
    initInlineLibraryEvents();
    
    // Sync mobile font dropdown with desktop fonts
    syncMobileFontDropdown();
    
    // Re-sync fonts after text tool manager loads (it populates desktop fonts)
    setTimeout(syncMobileFontDropdown, 1000);
    
    const runInitialSyncOnce = (reason = 'unspecified') => {
      if (mdsState.initialSyncCompleted) return;
      if (mdsState.initialSyncTimer) clearTimeout(mdsState.initialSyncTimer);
      mdsState.initialSyncTimer = setTimeout(() => {
        if (mdsState.initialSyncCompleted) return;
        mdsState.initialSyncCompleted = true;
        mdsState.initialSyncTimer = null;
        initializeSideNav();
        syncProductColors();
        if (typeof window.updatePrintSideOverlay === 'function') {
          setTimeout(() => window.updatePrintSideOverlay(mdsState.currentSide || 'front'), 300);
        }
        if (mdsState.pendingStartupColor) {
          try {
            if (typeof mdsState.refreshSideNavForColor === 'function') {
              mdsState.refreshSideNavForColor(mdsState.pendingStartupColor);
            }
          } catch (refreshErr) {
            console.warn('Initial side nav refresh skipped:', refreshErr?.message || refreshErr);
          }
          mdsState.pendingStartupColor = '';
        }
        console.log('Initial mobile sync completed:', reason);
      }, 20);
    };

    if (window.configuratorData) {
      runInitialSyncOnce('configuratorData:existing');
    } else {
      document.addEventListener('configuratorDataLoaded', () => {
        runInitialSyncOnce('configuratorDataLoaded');
      }, { once: true });

      // Fallback timeout
      setTimeout(() => {
        runInitialSyncOnce('fallback-timeout');
      }, 2000);
    }
    
    console.log('📱 Mobile Design Studio initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for external access
  window.mobileDesignStudio = {
    openPanel,
    closeAllPanels,
    switchSide,
    showDesignBadge,
    hideDesignBadge,
    updateDesignOverlay,
    handleExternalUpload,
    handleTextClick,
    initializeSideNav,
    attachDesignOverlayHandlers,
    syncDetectedColors,
    syncMobileBackgroundControls,
    syncProductColors,
    updateFloatingActionsVisibility,
    get state() { return mdsState; },
    get availableSides() { return mdsState.availableSides; },
    get currentSide() { return mdsState.currentSide; }
  };

  // Export mobileUpload interface for triggerUpload to use
  // This allows "Add Additional Image" and other upload triggers to open the mobile panel
  window.mobileUpload = {
    get isMobile() {
      return mdsState.isMobile;
    },
    handleMobileUpload(side, input, index) {
      console.log(`📱 mobileUpload.handleMobileUpload called for ${side}[${index}]`);
      // Set the current side so the upload goes to the right place
      if (side && side !== mdsState.currentSide) {
        switchSide(side);
      }
      // Store pending upload info locally (no global mutable state)
      mdsState.pendingMobileUpload = { side, index, input };
      inlineLibraryState.selectionToken = null;
      const libraryService = getLibraryService();
      if (libraryService && typeof libraryService.beginSelection === 'function') {
        inlineLibraryState.selectionToken = libraryService.beginSelection(side, index, { isEditMode: false });
      }
      // Open the mobile upload panel
      openPanel('upload');
    }
  };
  
  // Listen for color changes to update side thumbnails (on window, not document)
  // (Handled above in attachEventListeners; avoid duplicate listener)
  
  // Listen for configurator data loaded
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (mdsState.availableSides.length <= 1) {
        initializeSideNav();
      }
    }, 1500);
  });

})();
